/**
 * @file PricingCosting.js
 * @version 1.21.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-17
 * @modified 2026-11-03
 * @description Pricing & Costing (Cam and Jade's Pricing & Costing Review,
 *              decisions picked 17 Oct 2026). One screen, under Sales Orders
 *              and under Purchase Orders, the same either way (D10). The system
 *              proposes a cost and a sale price for every line delivering in
 *              the range, says where each came from, and sorts the lines into
 *              piles — Check, Needs a price, Ready, Asked a rep, Confirmed.
 *              Jade confirms; she only clicks into a line to choose something
 *              other than the proposal.
 *
 *   Layout A, the ledger (D1): one row per order line with what it was bought
 *   on (D2) — SO-D, its PO-D (or Xero PO) and the order's PO-H side by side; a
 *   stock PO-D line is a cost-only row, a book-to-account till draft's line a
 *   sale-only row. A row's Cost and Sale cells open its options.
 *
 *   Cost and sale are confirmed separately (D3) — the ✓ in each cell's
 *   column, or "Confirm costs" / "Confirm prices" / "Confirm both" on the
 *   ticked rows, or "Confirm N ready". Nothing is written until a person
 *   confirms (D6); Undo puts back what the line held. A later bill or price
 *   change that disagrees reopens a confirmed side under Check (D8).
 *
 *   Ask reps (D7): one email per rep, each with one spreadsheet of that rep's
 *   lines, checked and edited before it goes (1.3.0). A rep's filled-in
 *   spreadsheet is imported back, approved, held or kept as an option on each
 *   line ("Rep reply").
 *
 *   Draft invoices awaiting pricing are folded in (D9): a till draft's lines
 *   are rows; a row on an order with a draft offers "Approve invoice". Approving
 *   one dated last month asks first (D12, useDraftPriorMonth).
 *
 *   1.1.0 (Cam, 17 Oct): a row is the click target — one click shows its
 *   options under it, a double click ticks it; the actions are a ⋮ menu (and
 *   the same, plus the ticked rows' and the screen's own, on right-click) with
 *   an expand chevron beside it; every badge opens its window; the notes sit
 *   in a full-width row under their line; rows are banded, the options
 *   sharing their line's band; "Only lines" is a multi-select.
 *
 *   1.2.0 (Cam, 19 Oct): an "Invoiced" pile — orders invoiced from the
 *   Confirmed pile (whole orders, every line confirmed: row, ticked rows,
 *   right-click "Invoice confirmed orders"), each line then "Edit INV-…"
 *   (utils/editHandoff); Rep and product category filters; expand / collapse
 *   all; the tick box ticks again (the drag-select handlers wired as Price POs).
 *
 *   1.2.1 (Cam, 19 Oct): invoicing asks first when an order has a line with
 *   no supplied quantity recorded (named; "Invoice all" or only those with
 *   one) — the server refuses those orders unless the request names them.
 *
 *   1.3.0 (Cam, 19 Oct): asking reps is checked before anything goes — the
 *   "Send to reps" pile button (after All, a mailbox and a count) and the
 *   header button show the lines to ask about grouped by rep (tick all, a rep's,
 *   some or one), and "Review & send" opens RepSendModal: one email per rep,
 *   To / Cc / Subject / Body editable, and the rep's ONE spreadsheet as it will
 *   go. The tray is gone. A rep's returned prices can be approved or held; held
 *   ones sit in "Held rep prices" until approved or sent for a second check
 *   (the email and spreadsheet say so and show the earlier price). Right-click
 *   on one of several ticked rows shows only the bulk actions under a "Bulk"
 *   heading. Every action and every window's close reloads the lines.
 *
 *   1.4.0 (Cam, 19 Oct): a "Xero POs" pile — every goods line of a PO made in
 *   Xero (PO-…) with no order bought on it, priced here and invoiced PER LINE
 *   (XeroPoInvoiceModal) to the customer the PO names, or any other chosen in
 *   its options or the window; its rows sit in that pile only (and All).
 *   Lines can be removed from Send to reps (row, right-click, bulk) and put
 *   back. The Actions header is empty and its column stays 84px.
 *
 *   1.4.2 (Cam, 19 Oct — "click into a field to type a price and it
 *   collapses the row and seems to … scroll away … or move that row
 *   somewhere else"): choosing an option or typing a price recomputed the
 *   row's flags and pile on every keystroke, and the table is sorted (and the
 *   pile tabs filtered) by pile — so the row jumped to another section, or
 *   left the tab altogether, the moment it was touched, taking the focused
 *   input with it. A row being edited now HOLDS its place (pile section, sort
 *   position, page, tab) until the person clicks or tabs to something outside
 *   it (or collapses it, moves with J/K, confirms, changes tab or filter);
 *   then it moves, highlighted, with a toast naming where it went
 *   (utils/pricingCostingView). A click on any button, input, label or menu
 *   inside the row or its options never toggles it.
 *
 *   1.5.0 (Cam, 19 Oct — "group order rows by customer, subgrouped by product
 *   … so user does not need to duplicate work — same when sending to reps"):
 *   a "Group by customer › product" switch. A subgroup is what a price request
 *   to a rep asks once (the server's keys on each line), shown with its lines
 *   combined; ONE sale price for all its lines that can take it (each line's
 *   own option of that kind, or one typed figure), then Confirm costs / prices
 *   / both for the subgroup — each line through the ordinary confirm (same
 *   audit, guards and Undo), the lines that cannot take it skipped and named.
 *   A subgroup opens to its lines, each worked as before. Ticking a subgroup
 *   or a customer ticks its lines; pages hold whole subgroups. Sends to reps
 *   carry each line's cost as shown, and a request is split where they differ.
 *
 *   1.6.0 (Cam, 19 Oct — "each product group shares a single row, expandable
 *   to allow cost price and sale price for whole group, or user can split out
 *   into separate cost and sale prices per order … Right click a cost or sale
 *   price for a product group … set cost for group / set sale for group / set
 *   both for group, and then user can split the group and change some"): a
 *   product group is ONE row with the line columns; its Cost and Sale are one
 *   value for the whole group — opened (click / chevron) it offers cost AND
 *   sale options for all its lines, then the lines themselves. Each line takes
 *   the group's choice as its own, so editing a line on its own just makes the
 *   group read "Differs · 2 prices", and setting the group again makes them the
 *   same. Right-click (or ⋮) on a group: Set cost / sale / both for group…
 *   (PricingSetPopover — prefilled, "Set" or "Set and confirm", one Undo),
 *   Split group, Confirm cost / sale / both. On a line, and on the ticked lines
 *   (right-click, bulk bar): Set cost / sale / both…. A "Set" can be put back
 *   from its toast.
 *
 *   1.7.0 (Cam, 20 Oct 2026): the customer as its account-number badge and
 *   name (both open the contact, the search tables' .name-with-badge /
 *   .account-badge-inline), the first line of its delivery address faded
 *   underneath, then the supplier (name only); a group's item cell lists each
 *   delivery date (comma separated, not a range); SO / PO badges restyled; the
 *   notes worded "<what> — <action> to <effect>".
 *
 *   1.9.0 (Cam, 20 Oct 2026): customer sections fold — a chevron on each
 *   customer heading (its summary — products, lines, quantity, sale value,
 *   piles — readable on the heading alone); folding one closes its products,
 *   unfolding opens none of them; Expand all / Collapse all work on the
 *   customer sections. One product open at a time: opening a group or a
 *   line's options closes any other (customer sections are left alone). Rows
 *   outlined on hover instead of shaded, the open product outlined all the
 *   way round (its row, panel and lines), the customer heading made distinct.
 *   Rules: utils/pricingCostingView (openProduct, toggleLine, collapseCustomer…).
 *
 *   The rules are utils/pricingCosting.js (shared with the server), the data
 *   /api/pricing-costing (services/pricingCostingService.js).
 *
 * 1.10.0 (2026-10-23, Cam): a split order's tonnage added to the customer's
 * other order — the split line's sale reads "Added to SO-D… — invoiced there"
 * (never invoiced here on its own); the line taking it shows "+ 1.94 T from
 * SO-D…A (split of SO-D…)" and its margin is on the combined cost.
 *
 * 1.11.0 (2026-10-28, Cam — P1): "Sent to reps" in the header opens the
 * record of every price request sent to a rep (RepRequestHistory): download
 * the spreadsheet as sent, Resend it, or Send again (today's figures, through
 * RepSendModal with `resendOf`). Rows carry `rep_request` (last sent, to whom)
 * and `delivery_method` from the server for the Rep Pricing screen to come.
 *
 * 1.10.1 (2026-10-28, Cam): two fixes.
 *   P5 — "Right-clicking on the expanded content (when the cost/ sale price
 *   options show) causes a crash": every order line carries the server's
 *   request keys as `group`, and the right-click menu took that for its own
 *   product-group marker (`group.keys`), so right-clicking any order line —
 *   its row, or its options when open — threw and blanked the screen. The
 *   group row's marker is now `menuGroup`.
 *   P6 — "Clicking to the input field to manually type a cost/ sale price …
 *   behaves strangely - seems to scroll or collapse the view": focusing the
 *   box chose "Typed by hand" with no figure on the press of the mouse, so
 *   the line was re-laid out under the pointer before the click landed (the
 *   click then often fell on the box's label, which hands focus to the radio,
 *   and typing went nowhere), and a line only clicked into was left typed by
 *   hand with no price (in the Ready tab it left for Check). Now nothing
 *   changes until something is typed, and what is being worked on — the box
 *   typed in, the option chosen, the row clicked, the spot pressed when a
 *   held row is let go — is kept where it is on screen (keepInPlace: the
 *   table scrolls by however far it moved).
 *
 * 1.11.0 (2026-10-28, Cam — "Allow cost/ sale price figures to be applied per
 * tonne or per other unit from costing/ pricing (user can change there) …
 * and if there is a mismatch anywhere along the line … flag this via an icon
 * with tooltip explainer"): every typed cost / price box — a line's own, a
 * group's "Type one … for them all", the Set … popover — takes the figure per
 * the line's unit, per T or per kg (PerFigureInput, rules utils/pricePer),
 * stored per each line's own unit (milk powder, None and unknown units are
 * never converted; a group's per-tonne figure leaves them out, named). A
 * group in different units starts per tonne (tracker q03), one in a single
 * unit in that unit; the last switch is remembered (pricingCosting.enterPer).
 * A group whose lines differ in unit or figure shows them normalised per
 * tonne beside each unit's own, and its quantity as "40 × 25kg bag + 0.75 T
 * = 1.75 T" rather than one figure adding bags to tonnes. A line whose units
 * don't agree along the chain shows a warning icon by its product
 * (UnitFlagsIcon, the server's `unit_flags`); a group shows it for any of
 * its lines.
 *
 * 1.13.0 (2026-10-28, Cam — N4: "Add the option to right click/ three dots
 * menus (including when multiple selected) in pricing and costing to
 * 'Contract these prices' and handle as we will do the contract generation
 * from rep xlsx import. Also allow reps to flag for standard users if they
 * want contract generated from their prices"): "Contract these prices" on a
 * line's right-click and ⋮ menu, a product group's, the ticked lines' bulk
 * menu and the bulk bar opens ContractPricesModal — the import's own contract
 * panel (ContractFromPricesPanel), each line at its sale price as shown
 * (confirmed, else chosen, else proposed). Greyed with the reason without
 * 'Edit contract pricing'. A rep's open request for a contract
 * (`rep_contract_request`) shows on the line ("Rep asks for a contract",
 * hover: who, when, note), puts "Contract this price" first in its menu with
 * "Not now" beside it, and has its own pile, "Contract asked for".
 *
 * 1.14.0 (2026-10-28, Cam — the redesign's D3 "A second view inside Pricing &
 * Costing"): "Lines | Rep Pricing" at the top. ONE marked block (search
 * "rep-pricing-ui") — the switch in the header and the Rep Pricing view
 * (RepPricingView, its own file) shown instead of the lines, with the same
 * filters (dates carry over); the view is remembered per tab.
 *
 * 1.15.0 (2026-10-28, Cam — P8: "Pricing and costing component needs to be
 * streamlined, simplified, more clearly laid out - but the same functions
 * available"; the redesign's picks):
 *   D1 five stages in order on one row — To do · Needs a price › Check ›
 *   Ready › Confirmed › Invoiced · All. Rep work (Send to reps, Asked a rep,
 *   Held rep prices, Contract asked for) is Rep Pricing's view; each line
 *   still says its rep facts and its menus still send, approve, hold and
 *   contract. Cam's note: a line whose sale is with a rep is in Needs a price
 *   only while its cost is not set or confirmed; else it is counted "+ N
 *   costed, with reps" under Needs a price (a click shows them). Xero PO
 *   lines are the "Made in Xero" filter (off: every other line).
 *   D2 the four-step strip in place of Status: Cost · Sale · Confirmed ·
 *   Invoiced — green done, blue with a rep, amber needs a look; a group
 *   "2/3" (utils/pricingCostingView lineSteps / groupSteps).
 *   One heading line (help "?" for the instructions; More holds Import rep
 *   replies, Sent to reps, Clear filters, Refresh; Undo and Confirm N ready
 *   labelled), one filter row, the "Lines | Rep Pricing" switch (1.14.0)
 *   in the heading; Rep Pricing shares these filters AND the remembered
 *   range (one dropdown — rangeOptions — on both views).
 *   The customer on its heading only; the Product cell carries the delivery
 *   method, the supplier, ordered and supplied quantities in their units —
 *   "No supplied qty yet — needed before invoicing" (Cam's D7 note).
 *   Every cost / price figure and option says its unit (pricePer
 *   perUnitLabel: "/T", "/25kg bag", "/bag", "/each", "/unit?").
 *   The date range: a dropdown — this month, two before, two after by name,
 *   Custom — remembered per person (useScreenPreference
 *   'pricingCosting.range', Cam: "only default on first use … and just
 *   remember whatever they've last chosen").
 *   Earlier sales under the sale options, each with its own cost and margin
 *   (PricingEarlierSales — D3 / D10 notes); a product group's costs this
 *   period with the highest marked and the usual supplier, and what other
 *   customers pay (the office only) — PricingGroupCosts (P9, D6).
 *
 * 1.14.1 (2026-10-28, N6): what a figure is per is utils/unitPriceLabel's one
 * rule (pricePer re-exports its perUnitLabel): " each" for None, " (unit not
 * set)" for an unknown unit (were "/each", "/unit?"), a bulk product with no
 * weight per tonne. A None line's quantity is a count ("Ordered 1 each"), never
 * "(no unit)" — None is a unit.
 *
 * 1.15.0 (2026-10-29, N9 — the redesign's callout E, "options collapse when
 * sources agree"): in a line's cost and sale options, a product group's
 * options panel and the Set popover, options giving the same figure (per the
 * line's own unit, to 4 dp) are ONE option — the figure once, then each source
 * that gives it ("Bill FSIN50376235 · Price change 01/10 · Item cost"), the
 * proposed marker on its own source, "3 sources agree · Show each" for each
 * source's words. Choosing it chooses the proposal when that is one of them,
 * else the highest in the ladder (utils/pricingCostingView optionEntries /
 * entryChoice), so what is proposed, recorded, confirmed and undone is as
 * before. Options that differ stay separate, in ladder order.
 *
 * 1.16.0 (2026-10-29, Cam): Clear Filters is a button again, left of
 * "Lines | Rep Pricing" (out of More); the delivery dates are laid out as
 * Search Customer Orders' — From · To · the months under the arrow; the
 * stages sit wider apart after To do, and All at the end of the row. Stock
 * purchases are no longer rows (pricingCostingService 1.21.0).
 *
 * 1.16.1 (2026-10-29, Cam: "The margin column … is not wide enough and the
 * cell contents overflow the adjacent cell … it needs to handle whatever
 * width the td content needs"): the Margin column is as wide as its widest
 * figure on the page (measured after each draw, never under 104px) — the
 * table is fixed-layout, so a column cannot size itself to its content.
 *
 * 1.17.0 (2026-11-02, Cam — "the invoice date should be the delivery date on
 * the order not the date the invoice is generated … it would not invoice if
 * supplied qty missing (user can then go and confirm them)"): Invoice is
 * greyed on an order with a line that has no supplied quantity, saying to
 * record it (Add Supplied Qty on the order) — no "invoice anyway at the
 * ordered quantity" any more; the question says each invoice is dated its
 * delivery date; a date in a month that should be locked is asked first
 * (usePriorMonthConfirm — the server's 409 PRIOR_MONTH_CONFIRM).
 *
 * 1.19.0 (2026-11-03, Cam — "Booking over: need to be able to easily change
 * the invoice date and due date to a different month than the order, adding
 * a note to the sales description (manual entry)", and "Booking over can
 * often need splitting over multiple months"): every Invoice action opens
 * InvoiceBookOverDialog in place of the plain question — "The delivery date"
 * (as before, the default) or "Book over to another date" (invoice date, due
 * date, the note on every line; one order may be split over 2–12 invoices) —
 * and sends what was chosen as `bookOver` with /invoice/start.
 *
 * 1.20.0 (2026-11-03, Cam — "Need to be able to set invoices as approved/
 * authorised local-only ready to send on statements even when stock issues
 * or whatever block this going to Xero"): approving draft invoices, those Xero
 * refuses (409 XERO_REFUSED_APPROVE_HERE) — and any approved whose send to
 * Xero then failed (`xero_refused`) — are asked about once, listed with
 * Xero's reasons (useApproveHereOnly): "Approve here only" resends them with
 * approveHereOnly (the rest are approved as normal), or holds the approved
 * ones (POST /api/invoices/:id/hold-here). They go on statements now and to
 * Xero once Xero takes them; a persistent alert stays until then.
 * 1.20.1 (2026-11-03, Cam — booking over an invoice already raised): an
 * invoiced row's menus have "Book over INV-…" (BookOverInvoiceFlow — move it
 * to another date or split it while unpaid); the lines are read again after.
 * 1.21.0 (2026-11-03, Cam — "The component should sit in pricing and costing
 * header as a button left of the undo button and draw on the date pickers in
 * pricing and costing … The confirm and undo buttons can be slideout not
 * always expanded please, and give undo color-error background and confirm
 * color-success please, then this variation review button can take
 * color-primary"): **Variation Review** (show_chart, the old Variation Summary
 * button's icon) left of Undo, on var(--color-primary), opening VariationReview
 * over this screen with its period filled in from this screen's range (its
 * own afterwards — changing it there leaves this range alone); greyed without
 * 'variation_review.use'. Undo (var(--color-error)) and Confirm N ready
 * (var(--color-success)) are ordinary slide-outs now, not held open. Takes
 * `user` (Dashboard passes it).
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../config';
import { RepRequestHistoryModal } from './RepRequestHistory';
import './SearchLayout.css';
import './TableStyles.css';
import './SearchCustomerOrders.css';
import './PricingCosting.css';
import Input from './Input';
import Dropdown from './Dropdown';
import DateRangeFilter from './DateRangeFilter';
import MultiSelectDropdown from './MultiSelectDropdown';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import SearchHelp from './SearchHelp';
import TickCheckbox from './TickCheckbox';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import InvoicingProgressModal from './InvoicingProgressModal';
import InvoiceBookOverDialog from './InvoiceBookOverDialog';
import BookOverInvoiceFlow from './BookOverInvoiceFlow';
import RepSendModal from './RepSendModal';
import XeroPoInvoiceModal from './XeroPoInvoiceModal';
import SearchableDropdown from './SearchableDropdown';
import PricingHistoryModal from './PricingHistoryModal';
import RepRepliesImportModal from './RepRepliesImportModal';
import ContractPricesModal from './ContractPricesModal';
import VariationReview from './VariationReview';
import { hasPermission } from '../utils/permissions';
// rep-pricing-ui (D3): the Rep Pricing view and the "Lines | Rep Pricing" switch.
import RepPricingView, { useRepPricingSwitch } from './RepPricingView';
import PricingSetPopover from './PricingSetPopover';
import PerFigureInput from './PerFigureInput';
import UnitFlagsIcon from './UnitFlagsIcon';
import {
  sideChoicesPer, enteredOf, defaultPer, perOptions, toLineUnit, plainFigure, lineKgOf, unitsMixed,
  normalisedText, groupQtyText, PER_UNIT, PER_TONNE, perUnitLabel, fromLineUnit
} from '../utils/pricePer';
import PricingGroupCosts from './PricingGroupCosts';
import PricingEarlierSales from './PricingEarlierSales';
import useScreenPreference from '../hooks/useScreenPreference';
import { useSlideOutButton } from './SlideOutButton';
import { clearFiltersClass } from '../utils/activeFilters';
import { useEntityModal } from './EntityModalProvider';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import useReadOnly from '../hooks/useReadOnly';
import useDragSelect from '../hooks/useDragSelect';
import useTableContextMenu from '../hooks/useTableContextMenu';
import useDraftPriorMonth, { priorMonthPlanOf } from '../hooks/useDraftPriorMonth';
import useApproveHereOnly from '../hooks/useApproveHereOnly';
import { approveHereOfferOf, holdHere } from '../utils/heldHere';
import usePriorMonthConfirm from '../hooks/usePriorMonthConfirm';
import { startEdit } from '../utils/editHandoff';
import { matchesAllTerms } from '../utils/searchMatch';
import { formatQuantity, formatCount } from '../utils/numberFormatters';
import { formatDate } from '../utils/dateFormatters';
import {
  flagsFor, pileOf, stageOf, money, STAGE_LABELS, PRODUCT_CATEGORIES, productCategoryOf,
  repContractRequestText, repFlagText, saleWithRep
} from '../utils/pricingCosting';
import { combinedMarginCost } from '../utils/tonnageAdditions';
import {
  placeOf, holdRow, releaseHolds, movedOnRelease, movedNote, chosenFor, withChoice,
  buildGroups, pageGroups, sideSummary, differsText, groupSideOptions, flagSummary, costsForReps,
  deliveryDatesText, optionEntries, entryGroupChoice,
  productsByLine, customerSections, isProductOpen, toggleProduct, toggleLine, isCustomerCollapsed, toggleCustomer,
  collapseAllCustomers, expandAllCustomers, allCustomersExpanded, customerSummary,
  lineSteps, groupSteps, STEP_STATE_WORDS, repSaleText, rangeOptions, rangeOf, rangeBlock
} from '../utils/pricingCostingView';

// The five stages in order (Cam, 28 Oct 2026 — D1), To do first and All last.
// Rep work (Send to reps, Asked a rep, Held rep prices, Contract asked for)
// is Rep Pricing's; Xero PO lines are the "Made in Xero" filter, not a pile.
const STAGE_TABS = [
  { key: 'todo', label: 'To do', note: 'not confirmed', side: true },
  { key: 'needs', label: 'Needs a price', dot: 'needs', note: 'no cost or price to propose' },
  { key: 'check', label: 'Check', dot: 'check', note: 'something to look at' },
  { key: 'ready', label: 'Ready', dot: 'ready', note: 'to confirm' },
  { key: 'done', label: 'Confirmed', dot: 'done', note: 'to invoice' },
  { key: 'invoiced', label: 'Invoiced', dot: 'invoiced', note: 'sale read only' },
  { key: 'all', label: 'All', note: '', side: true }
];
// The order rows sit in, within a group's lines and for the pages.
const STAGE_ORDER = ['needs', 'with_reps', 'check', 'ready', 'done', 'invoiced'];
// The stage tabs a session may have remembered from before (old piles) → To do.
const KNOWN_TABS = new Set([...STAGE_TABS.map(t => t.key), 'with_reps']);

const STATUS_FILTERS = [
  { key: 'delivered', label: 'Delivered', icon: 'package_2' },
  { key: 'billed', label: 'Billed', icon: 'receipt_long' },
  { key: 'invoiced', label: 'Invoiced', icon: 'request_quote' },
  { key: 'confirmed', label: 'PO confirmed', icon: 'task_alt' },
  // Order lines only (Cam, 19 Oct): a supplied quantity recorded, or not yet.
  { key: 'has_supplied', label: 'Has supplied qty', icon: 'inventory' },
  { key: 'no_supplied', label: 'No supplied qty', icon: 'pending' }
];
const SUPPLIED_KEYS = ['has_supplied', 'no_supplied'];
/** A line against the "Only lines" choices: every status ticked; of the two supplied ones, either. */
const matchesOnly = (v, chosen) => {
  const status = chosen.filter(k => !SUPPLIED_KEYS.includes(k));
  if (status.length && !status.every(k => v.status?.[k])) return false;
  const supplied = chosen.filter(k => SUPPLIED_KEYS.includes(k));
  if (supplied.length) {
    if (v.kind !== 'order_line') return false;
    const has = v.supplied !== null && v.supplied !== undefined;
    if (!supplied.some(k => (k === 'has_supplied' ? has : !has))) return false;
  }
  return true;
};
const STATUS_ICONS = [
  ['po', 'shopping_cart', 'PO raised'],
  ['sent', 'mail', 'Sent to supplier'],
  ['confirmed', 'task_alt', 'PO confirmed'],
  ['transport', 'local_shipping', 'Transport planned'],
  ['delivered', 'package_2', 'Delivered'],
  ['billed', 'receipt_long', 'Billed'],
  ['invoiced', 'request_quote', 'Invoiced']
];
const SOURCE_ICONS = {
  bill: 'receipt_long', change: 'event', last_bill: 'history', item: 'inventory_2', current: 'assignment',
  contract: 'handshake', monthly: 'calendar_month', milk_powder: 'date_range', rep: 'forward_to_inbox',
  last_sold: 'history', margin: 'percent', invoice: 'request_quote', manual: 'edit', till: 'point_of_sale',
  xero_item: 'inventory_2'
};

// The date range is not here: it is the person's own, remembered across
// sessions (useScreenPreference 'pricingCosting.range' — Cam, 28 Oct 2026).
const CLEARED_FILTERS = { search: '', status: [], reps: [], categories: [], xero: false };
const NO_REP = '__none__';
// The Margin column's narrowest width; it widens to its widest figure (1.16.1).
const MARGIN_COL_MIN = 104;

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const qtyText = (r) => {
  if (!r.unit) return formatQuantity(r.qty);
  if (r.unit === 'T') return `${formatQuantity(r.qty)} T`;
  return `${formatCount(r.qty)} × ${r.unit}`;
};
/** Today in London, 'YYYY-MM-DD' (the server's days are London days). */
const londonToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());

function PricingCosting({ from = 'sales', onNavigate, user = null }) {
  const readOnly = useReadOnly();
  const { openEntity } = useEntityModal();
  const [filters, setFilters] = usePersistedState('pricingCosting.filters', CLEARED_FILTERS, { merge: true });
  const [tabRaw, setTab] = usePersistedState('pricingCosting.tab', 'todo');
  // Variation Review, over this screen (1.21.0).
  const [variationOpen, setVariationOpen] = useState(false);
  // A pile remembered from before the five stages reads as To do.
  const tab = KNOWN_TABS.has(tabRaw) ? tabRaw : 'todo';
  // The date range (Cam, 28 Oct 2026): "only default on first use … and just
  // remember whatever they've last chosen regarding date range thereafter" —
  // per person, across sessions. This month on first use; a named month stays
  // that month; This month follows the calendar.
  const today = londonToday();
  const [rangePref, setRangePref, rangeState] = useScreenPreference('pricingCosting.range', { preset: 'this-month' },
    { accept: (v) => !!v && typeof v === 'object' && typeof v.preset === 'string' });
  const range = useMemo(() => rangeOf(rangePref, today), [rangePref, today]);
  const rangeChoices = useMemo(() => rangeOptions(today, rangePref?.preset), [today, rangePref?.preset]);
  // Typed dates show at once; they load once they make a range the server takes.
  const [rangeDraft, setRangeDraft] = useState(null);
  const shownRange = rangeDraft || range;
  const rangeWhy = rangeBlock(shownRange.from, shownRange.to);
  const setRange = ({ from: f, to: t, preset }) => {
    const next = preset === 'custom' ? { preset: 'custom', from: f, to: t } : { preset };
    if (preset === 'custom' && rangeBlock(f, t)) { setRangeDraft({ preset: 'custom', from: f, to: t }); return; }
    setRangeDraft(null);
    setRangePref(next);
  };
  const rangeLoaded = !rangeState || rangeState.loaded !== false;
  // The filters as Rep Pricing (the other view) shares them: the same search /
  // rep / product / status, and the remembered range as from / to / preset —
  // a date or period chosen there is this person's remembered range too, so
  // the two views never disagree on the dates.
  const sharedFilters = useMemo(() => ({ ...filters, from: shownRange.from, to: shownRange.to, preset: rangeDraft ? 'custom' : range.preset }),
    [filters, shownRange.from, shownRange.to, rangeDraft, range.preset]);
  const setSharedFilters = (upd) => {
    const next = typeof upd === 'function' ? upd(sharedFilters) : upd;
    const { from: f, to: t, preset, ...rest } = next || {};
    if (f !== sharedFilters.from || t !== sharedFilters.to || preset !== sharedFilters.preset) setRange({ from: f, to: t, preset: preset || 'custom' });
    setFilters(prev => ({ ...prev, ...Object.fromEntries(Object.entries(rest).filter(([k]) => k in CLEARED_FILTERS)) }));
  };
  const [page, setPage] = usePersistedState('pricingCosting.page', 1);
  const [pageSize, setPageSize] = usePersistedState('pricingCosting.pageSize', '100');
  const [selected, setSelected] = usePersistedState('pricingCosting.selected', []);
  // A choice other than the proposal, per row and side: { id } or { id: 'manual', value }.
  const [choices, setChoices] = usePersistedState('pricingCosting.choices', {});
  // What a typed figure is per, as last chosen (1.11.0), per side:
  // { line_cost, line_sale, single_cost, single_sale, mixed_cost, mixed_sale }.
  const [enterPer, setEnterPer] = usePersistedState('pricingCosting.enterPer', {});
  // Which rows show their options: { key: true }.
  const [open, setOpen] = usePersistedState('pricingCosting.open', {});
  // Group by customer › product (1.5.0), and which subgroups show their lines.
  // Always grouped by customer › product (Cam, 20 Oct 2026: "Let's not make
  // grouping an option. Just make the way it is"); a group of one line reads
  // as an ordinary row.
  const groupBy = true;
  const [openGroups, setOpenGroups] = usePersistedState('pricingCosting.openGroups', {});
  // Customer sections folded to their heading (1.9.0): { customer block key: true }; open by default.
  const [collapsed, setCollapsed] = usePersistedState('pricingCosting.collapsedCustomers', {});
  // What is open as one state (utils/pricingCostingView): read from a ref so a
  // click's delayed toggle works on what is open then, not at the click.
  const openStateRef = useRef(null);
  openStateRef.current = { open, openGroups, collapsed };
  const setOpenState = (next) => {
    openStateRef.current = next;
    setOpen(next.open);
    setOpenGroups(next.openGroups);
    setCollapsed(next.collapsed);
  };
  // Lines a group's choice left as they were (no such option), by `group|side`.
  const [groupSkips, setGroupSkips] = useState({});
  // Options shown as one (1.15.0): which have "Show each" open.
  const [eachOpen, setEachOpen] = useState({});
  // "Set cost / sale / both…" (1.6.0): { title, keys, sides, gk, x, y }, or null.
  const [setAsk, setSetAsk] = useState(null);
  // Where the pointer last went down: the popover opens there.
  const pointRef = useRef({ x: 200, y: 200 });
  const [data, setData] = useState({ rows: [], counts: {}, can: {}, ready: true, range: null, loaded: false });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [focusKey, setFocusKey] = useState(null);
  // Rows being edited, held where they were (key → the pile they had), and
  // rows just released that moved (key → true, highlighted for a moment).
  const [holds, setHolds] = useState({});
  const [moved, setMoved] = useState({});
  const holdsRef = useRef(holds);
  holdsRef.current = holds;
  const [busy, setBusy] = useState(false);
  // Invoicing runs as a job on the server (Cam, 3 Nov 2026): its id is kept
  // for the tab, so leaving the screen and coming back shows it again.
  const [invoiceJob, setInvoiceJob] = usePersistedState('pricingCosting.invoiceJob', null);
  const [toast, setToast] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [alert, setAlert] = useState({ isOpen: false, message: '', type: 'info' });
  // "Book over INV-…" on an invoiced row (BookOverInvoiceFlow — 1.20.1).
  const [bookingOverId, setBookingOverId] = useState(null);
  const [confirmAsk, setConfirmAsk] = useState(null);
  // The Invoice window (3 Nov 2026): the delivery date, or booked over.
  const [invoiceAsk, setInvoiceAsk] = useState(null);
  // Lines in the "Send to reps" window (order line ids), or null.
  const [sendLines, setSendLines] = useState(null);
  // Xero PO lines to invoice (the window), and the customers to choose from (loaded when first needed).
  const [xeroInvoiceRows, setXeroInvoiceRows] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [importing, setImporting] = useState(false);
  // "Contract these prices" (N4): { lines, notLines } for the window, or null.
  const [contractLines, setContractLines] = useState(null);
  // The record of what went to which rep (P1, 28 Oct 2026) — until Rep Pricing holds it.
  const [showSent, setShowSent] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const latest = useLatestRequest();
  const priorMonth = useDraftPriorMonth();
  const approveHere = useApproveHereOnly();
  // Invoicing: an invoice dated in a month that should be locked is asked first.
  const invoicePriorMonth = usePriorMonthConfirm();
  const tableRef = useRef(null);
  // The Margin column fits its widest figure (1.16.1). Its figures never
  // wrap, so each one's own width is what it needs; measured after every
  // draw and set only when it moves, so it costs one extra draw at most.
  const [marginColW, setMarginColW] = useState(MARGIN_COL_MIN);
  useLayoutEffect(() => {
    const table = tableRef.current?.querySelector('table.pc-table');
    if (!table) return;
    let widest = 0;
    table.querySelectorAll('td.pc-margin .pc-val, td.pc-margin .pc-src').forEach((el) => {
      widest = Math.max(widest, Math.ceil(el.getBoundingClientRect().width));
    });
    const cell = table.querySelector('td.pc-margin');
    const cs = cell ? window.getComputedStyle(cell) : null;
    const pad = cs ? (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0) : 16;
    const want = Math.max(MARGIN_COL_MIN, widest + pad + 2);
    setMarginColW(w => (Math.abs(w - want) > 1 ? want : w));
  });
  const rootRef = useRef(null);
  useSlideOutButton([selected.length, tab, busy, data.loaded, undoStack.length, data.counts?.ready]);

  // ---- loading ----
  const load = useCallback(async () => {
    // Wait for the remembered range before the first read (no double load).
    if (!rangeLoaded) return;
    const ticket = latest.begin();
    setLoading(true);
    setLoadError(null);
    try {
      const qs = new URLSearchParams({ from: range.from || '', to: range.to || '' });
      const res = await fetch(`${API_URL}/api/pricing-costing/rows?${qs}`, { credentials: 'include' });
      const body = await res.json();
      if (!latest.isLatest(ticket)) return;
      if (!res.ok || !body.success) throw new Error(body.error || 'The lines could not be loaded');
      setData({ ...body, loaded: true });
    } catch (err) {
      if (latest.isLatest(ticket)) setLoadError(err.message);
    }
    if (latest.isLatest(ticket)) setLoading(false);
  }, [range.from, range.to, rangeLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // A filter change goes back to the first page; the first run keeps the persisted one.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setPage(1);
  }, [filters.search, range.from, range.to, filters.xero, (filters.status || []).join(','), (filters.reps || []).join(','), (filters.categories || []).join(','), tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (message, withUndo = false, action = null) => {
    setToast({ message, withUndo, action, at: Date.now() });
  };
  useEffect(() => {
    const at = (e) => { pointRef.current = { x: e.clientX, y: e.clientY }; };
    document.addEventListener('pointerdown', at, true);
    document.addEventListener('contextmenu', at, true);
    return () => {
      document.removeEventListener('pointerdown', at, true);
      document.removeEventListener('contextmenu', at, true);
    };
  }, []);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), toast.withUndo || toast.action ? 9000 : 5000);
    return () => clearTimeout(t);
  }, [toast]);

  // ---- each row as chosen on screen ----
  const rawViews = useMemo(() => data.rows.map(r => {
    const side = (name) => {
      const s = r[name];
      if (!s || !s.applies) return s || { applies: false };
      return { ...s, chosen: chosenFor(s, choices[`${r.key}|${name}`]) };
    };
    const cost = side('cost');
    const sale = side('sale');
    let marginCost = r.marginCost;
    if (cost.applies) {
      const base = cost.confirmed && !cost.reopened ? num(cost.confirmed.value) : num(cost.chosen?.value);
      marginCost = base === null ? null : base + (num(r.haulage?.per_unit) || 0);
    }
    const flags = flagsFor({ cost, sale, haulage: r.haulage, marginCost, per: r.per });
    const pile = pileOf({ cost, sale, flags });
    // The stage it reads in (D1) — placed by it, or by the one it had when an edit began (1.4.2).
    const stage = stageOf({ cost, sale, flags, pile });
    const saleValue = sale.applies
      ? (sale.locked ? num(sale.current) : (sale.confirmed && !sale.reopened ? num(sale.confirmed.value) : num(sale.chosen?.value)))
      : null;
    const margin = marginCost && saleValue ? { abs: saleValue - marginCost, pc: (saleValue - marginCost) / marginCost * 100 } : null;
    // Placed by the pile it was held at while it is being edited (1.4.2).
    return { ...r, cost, sale, flags, pile, stage, place: placeOf(stage, holds[r.key]), margin, saleValue, marginCost };
  }), [data.rows, choices, holds]);
  // A line taking a split order's tonnage (Cam, 23 Oct 2026): its margin is
  // on the combined cost — its own quantity at its cost and the added tonnage
  // at the split line's cost as chosen on screen (utils/tonnageAdditions).
  const views = useMemo(() => {
    const byKey = new Map(rawViews.map(v => [v.key, v]));
    return rawViews.map(v => {
      const t = v.tonnage;
      if (!t || !t.added?.length || !v.cost?.applies || v.marginCost === null || v.marginCost === undefined) return v;
      const parts = t.added.map(a => ({ quantity: a.quantity, sourceQuantity: a.source_quantity, sourceCost: byKey.get(a.key)?.marginCost ?? null }));
      const marginCost = combinedMarginCost(v.marginCost, t.own_qty, parts);
      const flags = flagsFor({ cost: v.cost, sale: v.sale, haulage: v.haulage, marginCost, per: v.per });
      const pile = pileOf({ cost: v.cost, sale: v.sale, flags });
      const stage = stageOf({ cost: v.cost, sale: v.sale, flags, pile });
      const margin = marginCost && v.saleValue ? { abs: v.saleValue - marginCost, pc: (v.saleValue - marginCost) / marginCost * 100, combined: true } : null;
      return { ...v, marginCost, flags, pile, stage, place: placeOf(stage, holds[v.key]), margin };
    });
  }, [rawViews, holds]);
  const viewByKey = useMemo(() => new Map(views.map(v => [v.key, v])), [views]);
  // Each order line's cost as shown — the import of a rep's sheet checks it
  // against the cost the request was asked at (1.5.0).
  const repLineFacts = useMemo(() => {
    const out = {};
    Object.entries(costsForReps(views)).forEach(([id, cost]) => { out[id] = { cost }; });
    return out;
  }, [views]);
  const viewsRef = useRef(viewByKey);
  viewsRef.current = viewByKey;

  // Every filter but the pile: the piles' counts follow what is filtered.
  const matching = useMemo(() => {
    const status = filters.status || [];
    const reps = filters.reps || [];
    const cats = filters.categories || [];
    return views.filter(v => {
      // "Made in Xero" (D1): on, only the lines of POs made in Xero; off, every other line.
      if (!!filters.xero !== (v.kind === 'xero_po_line')) return false;
      if (status.length && !matchesOnly(v, status)) return false;
      if (reps.length && !reps.includes(v.rep?.name || NO_REP)) return false;
      if (cats.length && !cats.includes(productCategoryOf(v.item?.range))) return false;
      if (filters.search && !matchesAllTerms([
        ...v.refs.map(x => x.label), v.customer?.name, v.customer?.account_no, v.supplier?.name,
        v.item?.code, v.item?.name, ...(v.additives || []), v.rep?.name
      ], filters.search)) return false;
      return true;
    });
  }, [views, filters.search, filters.status, filters.reps, filters.categories, filters.xero]);

  // The stages' counts (D1), as placed (a row being edited counts where it
  // was): To do = not confirmed; Needs a price with "+ N costed, with reps"
  // under it (Cam's note) — those lines are in To do and All only.
  const counts = useMemo(() => {
    const c = { todo: 0, needs: 0, with_reps: 0, check: 0, ready: 0, done: 0, invoiced: 0, all: matching.length };
    matching.forEach(v => { c[v.place] = (c[v.place] || 0) + 1; });
    c.todo = c.all - c.done - c.invoiced;
    return c;
  }, [matching]);

  // Placed by `place`, not `stage`: a row being edited stays put (1.4.2).
  const filtered = useMemo(() => matching.filter(v => {
    if (tab === 'all') return true;
    if (tab === 'todo') return v.place !== 'done' && v.place !== 'invoiced';
    return v.place === tab;
  }).sort((a, b) => (STAGE_ORDER.indexOf(a.place) - STAGE_ORDER.indexOf(b.place))
    || String(a.delivery_date || '').localeCompare(String(b.delivery_date || ''))
    || String(a.refs[0]?.label || '').localeCompare(String(b.refs[0]?.label || ''))), [matching, tab]);

  const limit = Number(pageSize) || 100;
  // Grouped (1.5.0): customers › subgroups, pages of whole subgroups.
  const groupPages = useMemo(() => (groupBy ? pageGroups(buildGroups(filtered), limit) : null), [groupBy, filtered, limit]);
  const totalPages = groupPages ? Math.max(1, groupPages.length) : Math.max(1, Math.ceil(filtered.length / limit));
  const safePage = Math.min(page || 1, totalPages);
  const pageEntries = groupPages ? (groupPages[safePage - 1] || []) : null;
  const pageRows = pageEntries ? pageEntries.flatMap(e => e.request.rows) : filtered.slice((safePage - 1) * limit, safePage * limit);

  // ---- selection (select all / select a section — Cam: tick everything, untick what isn't ready) ----
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const setChecked = useCallback((key, on) => {
    setSelected(prev => {
      const has = prev.includes(key);
      if (on && !has) return [...prev, key];
      if (!on && has) return prev.filter(k => k !== key);
      return prev;
    });
  }, [setSelected]);
  // Shift-click ranges run over the lines on show (a closed group's are not).
  const shownKeys = pageEntries
    ? pageEntries.flatMap(e => (!collapsed[e.block.key] && (e.request.rows.length === 1 || openGroups[e.request.key]) ? e.request.rows : [])).map(r => r.key)
    : pageRows.map(r => r.key);
  // Products by line, and customer sections, over every page (1.9.0).
  const productIndex = useMemo(() => productsByLine(groupPages || []), [groupPages]);
  const sections = useMemo(() => customerSections(groupPages || []), [groupPages]);
  const drag = useDragSelect({ setChecked, orderedIds: shownKeys });
  const setMany = (keys, on) => setSelected(prev => {
    const s = new Set(prev);
    keys.forEach(k => (on ? s.add(k) : s.delete(k)));
    return [...s];
  });
  const allOnPage = pageRows.length > 0 && pageRows.every(r => selectedSet.has(r.key));
  const selectedRows = views.filter(v => selectedSet.has(v.key));

  // ---- confirming ----
  const itemFor = (v, side) => {
    const s = v[side];
    if (!s || !s.applies || s.locked) return null;
    if (s.confirmed && !s.reopened) return null;
    if (!s.writable) return null;
    const c = s.chosen;
    if (!c || !(num(c.value) > 0)) return null;
    return {
      key: v.key, side, value: num(c.value), source: c.id,
      source_ref: [c.label, c.detail].filter(Boolean).join(' · ').slice(0, 200),
      // The contract / month / milk powder price it came from: confirming
      // links the line to it (28 Oct 2026).
      ...(side === 'sale' && c.targetId ? { source_id: c.targetId } : {}),
      expect: side === 'sale' ? (num(s.current) ?? 0) : num(s.current)
    };
  };

  // `skipped` — lines a subgroup's confirm left out, each with why (1.5.0).
  const skippedText = (skipped) => (skipped.length
    ? `\n\nSkipped ${skipped.length}:\n${skipped.slice(0, 15).map(x => `• ${x.label}: ${x.why}`).join('\n')}${skipped.length > 15 ? `\n… and ${skipped.length - 15} more` : ''}`
    : '');
  const runConfirm = async (items, what, skipped = []) => {
    // Confirming is being done with the rows: they go where they belong.
    release();
    if (!items.length) {
      setAlert({ isOpen: true, type: 'info', message: `Nothing to confirm${what ? ` — ${what}` : ''}. A side needs a figure above £0 and must not be confirmed already.${skippedText(skipped)}` });
      return;
    }
    setBusy(true);
    try {
      const results = [];
      for (let i = 0; i < items.length; i += 500) {
        const res = await fetch(`${API_URL}/api/pricing-costing/confirm`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ items: items.slice(i, i + 500) })
        });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error || 'Nothing was confirmed');
        results.push(...(body.results || []));
      }
      const ok = results.filter(r => r.outcome === 'confirmed');
      const bad = results.filter(r => r.outcome !== 'confirmed');
      if (ok.length) setUndoStack(prev => [...prev.slice(-9), ok.map(r => ({ key: r.key, side: r.side }))]);
      // Choices made on screen are spent once confirmed.
      setChoices(prev => {
        const next = { ...prev };
        ok.forEach(r => { delete next[`${r.key}|${r.side}`]; });
        return next;
      });
      if (bad.length) {
        const label = (k) => views.find(v => v.key === k)?.refs?.map(x => x.label).join(' ') || k;
        setAlert({
          isOpen: true, type: ok.length ? 'warning' : 'error',
          message: `${ok.length} confirmed, ${bad.length} not:\n\n${bad.slice(0, 15).map(b => `• ${label(b.key)} (${b.side}): ${b.message}`).join('\n')}${bad.length > 15 ? `\n… and ${bad.length - 15} more` : ''}${skippedText(skipped)}`
        });
      } else if (skipped.length) {
        setAlert({ isOpen: true, type: 'warning', message: `${ok.length} confirmed — Undo puts them back.${skippedText(skipped)}` });
      } else {
        showToast(`${ok.length} confirmed`, true);
      }
      await load();
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  const confirmRows = (rows, sides) => runConfirm(rows.flatMap(v => sides.map(s => itemFor(v, s)).filter(Boolean)));
  // A subgroup's confirm (1.5.0): every line that can take it, through the
  // ordinary per-line confirm; the others named with why.
  const confirmGroup = (rows, sides) => {
    const items = [];
    const skipped = [];
    const lineLabel = (v) => [v.refs?.[0]?.label, formatDate(v.delivery_date)].filter(Boolean).join(' · ');
    rows.forEach(v => sides.forEach(side => {
      if (!v[side]?.applies) return;
      const it = itemFor(v, side);
      if (it) items.push(it);
      else if (!(v[side].confirmed && !v[side].reopened)) {
        skipped.push({ label: `${lineLabel(v)} (${side === 'cost' ? 'cost' : 'sale'})`, why: canConfirmSide(v, side).why });
      }
    }));
    return runConfirm(items, null, skipped);
  };

  const undo = async () => {
    const last = undoStack[undoStack.length - 1];
    if (!last) return;
    setBusy(true);
    setToast(null);
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/unconfirm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ items: last })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Undo failed');
      setUndoStack(prev => prev.slice(0, -1));
      const bad = (body.results || []).filter(r => r.outcome === 'failed');
      if (bad.length) setAlert({ isOpen: true, type: 'warning', message: `Not undone:\n${bad.map(b => `• ${b.message}`).join('\n')}` });
      else showToast(`Undone — ${last.length} put back`);
      await load();
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  const unconfirmOne = async (v, side) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/unconfirm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ items: [{ key: v.key, side }] })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Undo failed');
      const bad = (body.results || []).filter(r => r.outcome === 'failed');
      if (bad.length) setAlert({ isOpen: true, type: 'warning', message: bad.map(b => b.message).join('\n') });
      await load();
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  // ---- asking reps (D7; checked before sending, 1.3.0) ----
  function sendable(v) {
    return v.kind === 'order_line' && !!v.sale?.applies && !v.sale.locked && !(v.sale.confirmed && !v.sale.reopened);
  }
  const sendBlock = (rows) => (readOnly ? 'View only'
    : !data.can.confirm_sales ? 'Asking reps needs permission to price sales orders'
      : !rows.some(sendable) ? 'Only an order line still to price can be asked about' : null);
  const exclusionBlock = (rows, exclude) => (readOnly ? 'View only'
    : !data.can.confirm_sales ? 'This needs permission to price sales orders'
      : !data.xero_lines_ready ? 'Not ready — migration pricing_costing_xero_po_lines.sql has not run'
        : !rows.some(v => sendable(v) && (exclude ? !v.sale?.notForReps : !!v.sale?.notForReps))
          ? (exclude ? 'None of these is in Send to reps' : 'None of these was taken out of Send to reps') : null);
  const runExclusion = async (rows, exclude) => {
    const ids = [...new Set(rows.filter(v => sendable(v) && (exclude ? !v.sale?.notForReps : !!v.sale?.notForReps)).map(v => v.order_line_id))];
    if (!ids.length) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-requests/exclude`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ lineIds: ids, exclude })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Nothing was changed');
      showToast(exclude
        ? `${ids.length} line${ids.length === 1 ? '' : 's'} taken out of Send to reps`
        : `${ids.length} line${ids.length === 1 ? '' : 's'} back in Send to reps`);
      if (exclude) setSelected(prev => prev.filter(k => !ids.includes(Number(String(k).replace(/^ol:/, '')))));
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
      await load();
    }
  };

  // ---- "Contract these prices" (N4, 28 Oct 2026) ----
  // A line's sale price as the screen shows it, and where it came from.
  const contractLineOf = (v) => {
    const s = v.sale || {};
    const id = v.order_line_id;
    if (s.locked) return { id, price: num(s.current), from: 'Invoiced' };
    if (s.confirmed && !s.reopened) return { id, price: num(s.confirmed.value), from: 'Confirmed' };
    const c = s.chosen;
    if (!c) return { id, price: null, from: null };
    const picked = !!choices[`${v.key}|sale`];
    return { id, price: num(c.value), from: `${picked ? 'Chosen' : 'Proposed'}: ${c.label}` };
  };
  const contractBlock = (rows) => (readOnly ? 'View only'
    : !data.can.contract ? (data.can.contract_block || "Needs 'Edit contract pricing'")
      : !rows.some(v => v.kind === 'order_line') ? 'Only a customer\'s order line can be made a contract price' : null);
  const openContract = (rows) => {
    release();
    const lines = [];
    const notLines = [];
    rows.forEach(v => {
      if (v.kind === 'order_line') lines.push(contractLineOf(v));
      else notLines.push({ key: v.key, label: v.refs.map(x => x.label).join(' '), why: 'Not a customer\'s order line — a contract is for a customer\'s orders' });
    });
    setContractLines({ lines, notLines });
  };
  const contractLabel = (n) => (n === 1 ? 'Contract this price…' : `Contract these prices (${n})…`);
  const asksContract = (v) => !!v.rep_contract_request;
  const notNowBlock = (rows) => (readOnly ? 'View only'
    : !data.can.confirm_sales ? 'Answering a rep needs permission to price sales orders'
      : !rows.some(asksContract) ? 'No rep has asked for a contract on these' : null);
  const runNotNow = async (rows) => {
    const ids = [...new Set(rows.filter(asksContract).map(v => v.order_line_id))];
    if (!ids.length) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-contract-requests/dismiss`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ lineIds: ids })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Nothing was changed');
      showToast(`Not now — ${body.dismissed} rep request${body.dismissed === 1 ? '' : 's'} for a contract answered`);
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
      await load();
    }
  };
  // "Rep asks for a contract" on a line (or how many of a group's lines).
  const contractAskBadge = (rows) => {
    const asking = rows.filter(asksContract);
    if (!asking.length) return null;
    const text = asking.length === 1 ? repContractRequestText(asking[0].rep_contract_request)
      : `${asking.length} lines: ${asking.slice(0, 3).map(v => repContractRequestText(v.rep_contract_request, { action: false })).join(' · ')}${asking.length > 3 ? ' …' : ''} — Contract these prices, or Not now.`;
    return (
      <span className="pc-contract-ask" title={text}>
        <span className="material-symbols-rounded">handshake</span>
        {rows.length === 1 ? 'Rep asks for a contract' : `Rep asks for a contract (${asking.length})`}
      </span>
    );
  };

  // ---- Xero PO lines: who they are invoiced to, and invoicing them per line ----
  const loadCustomers = async () => {
    if (customers) return customers;
    try {
      const res = await fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=CUSTOMER&limit=10000`, { credentials: 'include' });
      const body = await res.json();
      const list = body.contacts || [];
      setCustomers(list);
      return list;
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: `The customers could not be loaded: ${err.message}` });
      return null;
    }
  };
  const xeroInvoiceBlock = (rows) => {
    const xs = rows.filter(v => v.kind === 'xero_po_line');
    if (!xs.length) return 'Not a Xero PO line';
    if (readOnly) return 'View only';
    if (!data.can.approve_invoices) return "Invoicing needs the 'Create invoices' permission";
    if (!xs.some(v => !v.invoice)) return 'Already invoiced';
    return null;
  };
  const openXeroInvoice = async (rows) => {
    const xs = rows.filter(v => v.kind === 'xero_po_line' && !v.invoice);
    if (!xs.length) return;
    if (await loadCustomers()) setXeroInvoiceRows(xs);
  };
  const setXeroCustomer = async (v, accountId) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/xero-po-lines/customer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ keys: [v.key], accountId: accountId || null })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Not saved');
      const bad = (body.results || []).filter(r => r.outcome !== 'saved');
      if (bad.length) setAlert({ isOpen: true, type: 'warning', message: bad.map(b => b.message).join('\n') });
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
      await load();
    }
  };
  // What is on screen (the filters, Made in Xero included) — never every line
  // loaded (Cam, 3 Nov 2026: the right-click on C R Goucher's confirmed wheat
  // offered Willes Farming's milk powder from Xero POs, filtered out of view).
  const xeroReadyRows = matching.filter(v => v.kind === 'xero_po_line' && !v.invoice_block);

  // The cost each line will be confirmed at goes too: a request to a rep is
  // split where its lines' costs differ (1.5.0).
  const openSend = (rows) => {
    const send = rows.filter(sendable);
    const ids = [...new Set(send.map(v => v.order_line_id))];
    if (ids.length) setSendLines({ ids, costs: data.can.see_costs ? costsForReps(send) : {} });
  };

  // ---- a rep's returned price: approve or hold (the Held rep prices pile) ----
  const reviewable = (v) => v.kind === 'order_line' && !!v.sale?.repReply && v.sale.repReply.status !== 'approved'
    && !v.sale.locked && !(v.sale.confirmed && !v.sale.reopened);
  const reviewBlock = (rows, action) => (readOnly ? 'View only'
    : !data.rep_review_ready ? 'Not ready — migration pricing_costing_rep_review.sql has not run'
      : action === 'approve' && !data.can.confirm_sales ? 'Approving needs permission to price sales orders'
        : !rows.some(reviewable) ? 'No rep price to review on these lines' : null);
  const runReview = async (rows, action) => {
    const ids = [...new Set(rows.filter(reviewable).map(v => v.order_line_id))];
    if (!ids.length) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-replies/review`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ lineIds: ids, action })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Nothing was changed');
      const results = body.results || [];
      const good = results.filter(r => r.outcome === (action === 'approve' ? 'confirmed' : 'held'));
      const bad = results.filter(r => !good.includes(r));
      if (action === 'approve' && good.length) setUndoStack(prev => [...prev.slice(-9), good.map(r => ({ key: r.key, side: 'sale' }))]);
      if (bad.length) {
        setAlert({ isOpen: true, type: good.length ? 'warning' : 'error',
          message: `${good.length} ${action === 'approve' ? 'approved' : 'held'}, ${bad.length} not:\n\n${bad.slice(0, 15).map(b => `• ${b.message}`).join('\n')}` });
      } else {
        showToast(action === 'approve'
          ? `${good.length} rep price${good.length === 1 ? '' : 's'} approved — the sale price is confirmed`
          : `${good.length} rep price${good.length === 1 ? '' : 's'} held — in Held rep prices`, action === 'approve');
      }
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
      await load();
    }
  };

  // ---- draft invoices (D9, D12; approve here only 1.19.0) ----
  const approveDrafts = async (draftIds) => {
    const ids = [...new Set(draftIds)];
    if (!ids.length) return;
    setBusy(true);
    const done = [];
    const failed = [];
    const asked = [];
    // Xero refused: { id, invoice_no, reason, choice } — the approval
    // (`approve`) or, already approved here, its send (`hold`).
    const refused = [];
    const send = async (id, choice, extra = {}) => {
      const res = await fetch(`${API_URL}/api/draft-pricing/${id}/prices`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ lines: [], approve: true, source: 'Pricing & Costing', ...(choice ? { prior_month: choice } : {}), ...extra })
      });
      const body = await res.json();
      return { res, body };
    };
    // One answer, whichever request it came from.
    const take = (id, label, choice, res, body) => {
      const offer = approveHereOfferOf(res.status, body);
      if (offer) { refused.push({ id, kind: 'approve', invoice_no: offer.invoice_no || label, reason: offer.reason, choice, ready: offer.ready !== false, notReady: offer.not_ready_reason }); return; }
      if (!res.ok || !body.success) {
        const notReady = body?.details?.approve_here && body.details.approve_here.ready === false ? ` (${body.details.approve_here.not_ready_reason})` : '';
        failed.push(`${body.invoice_no || label}: ${body.error || 'not approved'}${notReady}`);
      } else if (!body.approved) failed.push(body.approval_block || `${body.invoice_no || label} was not approved`);
      else {
        done.push(body);
        if (body.xero_refused) refused.push({ id: body.xero_refused.invoice_id, kind: 'hold', invoice_no: body.xero_refused.invoice_no, reason: body.xero_refused.reason, ready: body.xero_refused.ready !== false, notReady: body.xero_refused.not_ready_reason });
      }
    };
    try {
      for (const id of ids) {
        const { res, body } = await send(id, null);
        const plan = priorMonthPlanOf(res.status, body);
        if (plan) { asked.push({ id, plan }); continue; }
        take(id, `Draft ${id}`, null, res, body);
      }
      if (asked.length) {
        setBusy(false);
        const choice = await priorMonth.ask(asked.map(a => a.plan));
        setBusy(true);
        if (choice) {
          for (const a of asked) {
            const { res, body } = await send(a.id, choice);
            take(a.id, a.plan.invoice_no, choice, res, body);
          }
        } else {
          asked.forEach(a => failed.push(`${a.plan.invoice_no}: left as a draft (not answered)`));
        }
      }
      // Xero refused some: one question for them all, the rest already done.
      const askable = refused.filter(r => r.ready);
      refused.filter(r => !r.ready).forEach(r => failed.push(`${r.invoice_no}: Xero refused it — ${r.reason}. Approving it here only is not possible yet: ${r.notReady || 'not ready'}`));
      const heldNow = [];
      if (askable.length) {
        setBusy(false);
        const yes = await approveHere.ask(askable.map(r => ({ invoice_no: r.invoice_no, reason: r.reason })));
        setBusy(true);
        for (const r of askable) {
          if (!yes) {
            failed.push(r.kind === 'approve'
              ? `${r.invoice_no}: Xero refused it — ${r.reason}. Left as a draft.`
              : `${r.invoice_no}: approved here; Xero refused it on its way — ${r.reason}. It is in the "not in Xero" alert.`);
            continue;
          }
          try {
            if (r.kind === 'approve') {
              const { res, body } = await send(r.id, r.choice, { approveHereOnly: true });
              if (!res.ok || !body.success) failed.push(`${r.invoice_no}: ${body.error || 'not approved'}`);
              else if (!body.approved) failed.push(body.approval_block || `${r.invoice_no} was not approved`);
              else { done.push(body); heldNow.push(body.held_here ? r.invoice_no : null); }
            } else {
              await holdHere(r.id);
              heldNow.push(r.invoice_no);
            }
          } catch (err) {
            failed.push(`${r.invoice_no}: ${err.message}`);
          }
        }
      }
      const held = heldNow.filter(Boolean);
      const lines = [
        done.length ? `${done.length} invoice${done.length === 1 ? '' : 's'} approved: ${done.map(d => d.invoice_no).join(', ')}.` : '',
        held.length ? `Approved here only, not yet in Xero: ${held.join(', ')} — on statements now, sent to Xero once Xero takes ${held.length === 1 ? 'it' : 'them'}; an alert stays until then.` : '',
        ...done.filter(d => d.redated).map(d => d.redated.message),
        ...done.filter(d => d.xero_warning && !d.xero_refused).map(d => `${d.invoice_no}: ${d.xero_warning}`),
        ...(failed.length ? ['', 'Not approved:', ...failed.map(f => `• ${f}`)] : [])
      ].filter((l, i, a) => l !== '' || (i > 0 && a[i - 1] !== ''));
      setAlert({ isOpen: true, type: failed.length ? 'warning' : 'success', message: lines.join('\n') });
      await load();
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  const askApprove = (draftIds) => {
    const drafts = [...new Map(views.filter(v => v.draft && draftIds.includes(v.draft.id)).map(v => [v.draft.id, v.draft])).values()];
    if (!drafts.length) return;
    setConfirmAsk({
      title: drafts.length === 1 ? 'Approve invoice' : 'Approve invoices',
      message: `Approve ${drafts.map(d => d.invoice_no).join(', ')}? `
        + `${drafts.length === 1 ? 'It becomes an approved invoice' : 'They become approved invoices'} — here and in Xero, where approving takes the stock. `
        + 'Every line must have a price first. If Xero refuses one (no stock on its date, or Xero down), you are asked '
        + 'whether to approve it here only.',
      confirmText: 'Approve',
      onConfirm: () => approveDrafts(drafts.map(d => d.id))
    });
  };

  // ---- keyboard (J/K move, Enter confirm, C / S expand, R ask rep, U undo, X tick) ----
  // Grouped, only the lines on show (a closed subgroup's are not).
  const shownRef = useRef(null);
  const onKeyDown = (e) => {
    const tag = (e.target?.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.metaKey || e.ctrlKey || e.altKey) return;
    const k = e.key.toLowerCase();
    const navRows = shownRef.current ? pageRows.filter(r => shownRef.current.has(r.key)) : pageRows;
    const focusIndex = navRows.findIndex(r => r.key === focusKey);
    const move = (d) => {
      const i = Math.max(0, Math.min(navRows.length - 1, (focusIndex < 0 ? (d > 0 ? -1 : navRows.length) : focusIndex) + d));
      const r = navRows[i];
      if (r) {
        if (r.key !== focusKey) release();
        setFocusKey(r.key);
        const el = tableRef.current?.querySelector(`[data-row="${CSS.escape(r.key)}"]`);
        el?.scrollIntoView({ block: 'nearest' });
      }
    };
    const v = focusIndex >= 0 ? navRows[focusIndex] : null;
    if (k === 'j' || e.key === 'ArrowDown') { e.preventDefault(); move(1); return; }
    if (k === 'k' || e.key === 'ArrowUp') { e.preventDefault(); move(-1); return; }
    if (k === 'u') { e.preventDefault(); undo(); return; }
    if (!v) return;
    if (e.key === 'Enter') { e.preventDefault(); if (!readOnly && !busy) confirmRows([v], ['cost', 'sale']); return; }
    if (k === 'c' || k === 's') { e.preventDefault(); toggleOpen(v.key); return; }
    if (k === 'r') { e.preventDefault(); if (!sendBlock([v])) openSend([v]); return; }
    if (k === 'x' || e.key === ' ') { e.preventDefault(); setChecked(v.key, !selectedSet.has(v.key)); }
  };

  // What the person is working on stays where it is on screen (Cam, 28 Oct
  // 2026 — "clicking … the input field … seems to scroll or collapse the
  // view"). A choice re-lays its line out (the cell's source, a note row) and
  // letting a held row go can take it out of the tab or close another
  // product's options above; the field being typed in, the option chosen or
  // the row clicked is put back under the pointer by scrolling the table by
  // however far it moved. Set just before the change, used once, on the next
  // commit.
  const anchorRef = useRef(null);
  const keepInPlace = useCallback((el) => {
    const box = tableRef.current;
    if (!el || !box || !box.contains(el)) return;
    anchorRef.current = { el, top: el.getBoundingClientRect().top, at: Date.now() };
  }, []);
  useLayoutEffect(() => {
    const a = anchorRef.current;
    if (!a) return;
    anchorRef.current = null;
    const box = tableRef.current;
    if (!box || !a.el.isConnected || Date.now() - a.at > 1000) return;
    const moved = a.el.getBoundingClientRect().top - a.top;
    if (Math.abs(moved) >= 1) box.scrollTop += moved;
  });

  const isOpen = (key) => !!open[key];
  // A line's options: opening them makes its product the one open product
  // (1.9.0) — any other closes, and lets go of any row held in it. `rowEl`,
  // the row clicked, stays where it is when something above it closes.
  const toggleOpen = (key, rowEl = null) => {
    const st = openStateRef.current;
    const product = productIndex.get(key);
    if (st.open[key]) {
      if (holdsRef.current[key]) release();
    } else if (product) {
      release(product.lines);
    }
    keepInPlace(rowEl);
    setOpenState(toggleLine(st, key, product));
  };
  // An edit holds the row where it is (1.4.2) — its pile before the edit.
  const holdFor = (key) => {
    const next = holdRow(holdsRef.current, key, viewsRef.current.get(key)?.place);
    if (next !== holdsRef.current) { holdsRef.current = next; setHolds(next); }
  };
  const choose = (key, side, choice) => {
    holdFor(key);
    setChoices(prev => ({ ...prev, [`${key}|${side}`]: choice }));
  };
  const resetChoice = (key, side) => {
    holdFor(key);
    setChoices(prev => { const n = { ...prev }; delete n[`${key}|${side}`]; return n; });
  };
  // Let the held rows go where they now belong (all, or all but `keepKey`),
  // highlighting the ones that moved and saying where they went. `anchorEl`
  // (what was just clicked or focused) stays where it is on screen.
  const release = useCallback((keepKey = null, anchorEl = null) => {
    const { holds: next, released } = releaseHolds(holdsRef.current, keepKey);
    if (!released.length) return;
    keepInPlace(anchorEl);
    holdsRef.current = next;
    setHolds(next);
    const went = movedOnRelease(released, (k) => {
      const v = viewsRef.current.get(k);
      return v ? v.stage : null;
    });
    if (!went.length) return;
    const label = (k) => {
      const v = viewsRef.current.get(k);
      return [v?.refs?.[0]?.label, v?.item?.name || v?.item?.code].filter(Boolean).join(' · ') || 'The line';
    };
    setMoved(m => ({ ...m, ...Object.fromEntries(went.map(w => [w.key, true])) }));
    setToast({ message: movedNote(went, label, (p) => STAGE_LABELS[p] || p), withUndo: false, at: Date.now() });
  }, [keepInPlace]);
  // Done with a row = a click or focus anywhere on the screen outside it. A
  // portalled menu (a dropdown list) is outside the screen, so it keeps it.
  const holding = Object.keys(holds).length > 0;
  useEffect(() => {
    if (!holding) return undefined;
    const onOutside = (e) => {
      const root = rootRef.current;
      if (!root || !(e.target instanceof Node) || !root.contains(e.target)) return;
      const inside = e.target.closest?.('[data-hold]')?.getAttribute('data-hold') || null;
      // Released on the press, before the click lands: whatever was pressed
      // is kept under the pointer, so the click still lands on it.
      release(inside, e.target);
    };
    document.addEventListener('pointerdown', onOutside, true);
    document.addEventListener('focusin', onOutside, true);
    return () => {
      document.removeEventListener('pointerdown', onOutside, true);
      document.removeEventListener('focusin', onOutside, true);
    };
  }, [holding, release]);
  // A tab or filter change lets every held row go.
  useEffect(() => { release(); }, [tab, filters.search, range.from, range.to, filters.xero, (filters.status || []).join(','), (filters.reps || []).join(','), (filters.categories || []).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  // The highlight of a row that has just moved fades after a moment.
  useEffect(() => {
    if (!Object.keys(moved).length) return undefined;
    const t = setTimeout(() => setMoved({}), 2600);
    return () => clearTimeout(t);
  }, [moved]);

  const handleClear = () => {
    setFilters(CLEARED_FILTERS);
    setTab('todo');
  };
  // The date range is the person's own choice, not a filter to clear.
  const filtersApplied = !!(filters.search || (filters.status || []).length || (filters.reps || []).length
    || (filters.categories || []).length || filters.xero);
  // The reps on the lines in the range, for the Rep filter ("No rep" for lines without one).
  const repOptions = useMemo(() => {
    const names = [...new Set(views.map(v => v.rep?.name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    return [...names.map(n => ({ value: n, label: n })), { value: NO_REP, label: 'No rep' }];
  }, [views]);
  // Expand / collapse all (1.9.0, Cam): the CUSTOMER sections — Collapse all
  // leaves each customer a single row, Expand all opens every customer with
  // its products closed. The button reads off the customers on this page.
  const pageCustomerKeys = pageEntries ? [...new Set(pageEntries.map(e => e.block.key))] : [];
  const allOpen = pageEntries
    ? allCustomersExpanded({ collapsed }, pageCustomerKeys)
    : pageRows.length > 0 && pageRows.every(r => open[r.key]);
  const toggleAll = () => {
    if (pageEntries) {
      release();
      setOpenState(allOpen
        ? collapseAllCustomers(openStateRef.current, sections.map(c => c.key))
        : expandAllCustomers(openStateRef.current));
      return;
    }
    setOpen(prev => {
      const next = { ...prev };
      pageRows.forEach(r => { if (allOpen) delete next[r.key]; else next[r.key] = true; });
      return next;
    });
  };

  // ---- a row's clicks: one click expands / collapses, a double click ticks ----
  // A badge, button, link, input or the tick box keeps its own click.
  const clickTimer = useRef(null);
  const ownClick = (e) => !!e.target.closest('button, a, input, label, select, textarea, .pc-ref, .tick-check-label, [role="menu"]');
  const onRowClick = (v) => (e) => {
    if (ownClick(e)) return;
    setFocusKey(v.key);
    if (e.detail > 1) return;
    const rowEl = e.currentTarget;
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => toggleOpen(v.key, rowEl), 220);
  };
  const onRowDoubleClick = (v) => (e) => {
    if (ownClick(e)) return;
    clearTimeout(clickTimer.current);
    window.getSelection?.().removeAllRanges?.();
    setChecked(v.key, !selectedSet.has(v.key));
  };
  useEffect(() => () => clearTimeout(clickTimer.current), []);

  // ---- references: every badge opens its own window ----
  const openRef = (ref) => {
    if (ref.type === 'SO-D') return openEntity('customer_order', ref.id, { onClose: load });
    if ((ref.type === 'PO-D' || ref.type === 'PO' || ref.type === 'PO-H') && ref.xero_id) return openEntity('purchase_order', ref.xero_id, { onClose: load });
    if ((ref.type === 'INV' || ref.type === 'DRAFT') && ref.id) {
      return openEntity(/^CN/i.test(ref.label || '') ? 'credit_note' : 'invoice', ref.id, { onClose: load });
    }
    return null;
  };
  const refOpens = (ref) => ref.type === 'SO-D' ? !!ref.id
    : (ref.type === 'PO-D' || ref.type === 'PO' || ref.type === 'PO-H') ? !!ref.xero_id
      : (ref.type === 'INV' || ref.type === 'DRAFT') ? !!ref.id : false;

  // ---- cells (not buttons: the row is the click target) ----
  // Every figure says what it is per (Cam's D7 note) — utils/unitPriceLabel:
  // "/T", "/25kg bag", "/bag" for milk powder, " each" for None, " (unit not
  // set)" when nobody knows.
  const unitOf = (v) => {
    const u = perUnitLabel(v);
    return <span className={`pc-unit${u.known ? '' : ' pc-unit-unknown'}`} title={u.title}>{u.text}</span>;
  };
  const perText = (v) => perUnitLabel(v).text;
  const repFlagChip = (v) => (v.sale?.repFlag ? (
    <span className="pc-flagchip" title={repFlagText(v.sale.repFlag)}>
      <span className="material-symbols-rounded">flag</span>Flagged by {v.sale.repFlag.by || v.sale.repFlag.rep_name || 'the rep'}
    </span>
  ) : null);
  const sideCell = (v, side) => {
    const s = v[side];
    if (!s || !s.applies) {
      if (side === 'sale' && s?.addedTo) return <span className="pc-na" title={s.addedTo.note}>{s.addedTo.note}</span>;
      if (side === 'sale') return <span className="pc-na">Stock purchase — no sale</span>;
      // A till line has no cost to confirm: the till's cost and Xero's, side by side (1.8.0).
      if (v.kind === 'invoice_line') {
        const lines = v.cost?.infoLines || [];
        if (!lines.length) return <span className="pc-na">{v.cost?.info?.text || 'Till sale — from stock'}</span>;
        return (
          <div className="pc-cell pc-till-costs">
            {lines.map(l => (
              <span key={l.id} className="pc-src">
                <span className="material-symbols-rounded">{SOURCE_ICONS[l.id] || 'info'}</span>{l.label}{perText(v)}
              </span>
            ))}
            {v.cost.infoNote && <span className="pc-src pc-muted">{v.cost.infoNote}</span>}
          </div>
        );
      }
      return <span className="pc-na">{data.can.see_costs ? '—' : 'Hidden'}</span>;
    }
    if (side === 'sale' && s.locked) {
      return (
        <div className="pc-cell">
          <span className="pc-val">{money(s.proposed?.value ?? s.current)}{unitOf(v)}</span>
          <span className="pc-src"><span className="material-symbols-rounded">lock</span>Invoiced — read only</span>
          {repFlagChip(v)}
        </div>
      );
    }
    if (s.confirmed && !s.reopened) {
      return (
        <div className="pc-cell confirmed">
          <span className="pc-val">{money(s.confirmed.value)}{unitOf(v)}</span>
          <span className="pc-src ok"><span className="material-symbols-rounded">verified</span>Confirmed{s.confirmed.by ? ` by ${s.confirmed.by}` : ''}{s.confirmed.at ? ` · ${formatDate(s.confirmed.at)}` : ''}</span>
          {s.confirmed.source_ref && <span className="pc-src">{s.confirmed.source_ref}</span>}
          {side === 'sale' && repFlagChip(v)}
        </div>
      );
    }
    const c = s.chosen;
    // Where the sale price is with a rep (C): "With Andy Hawken since 20/10 (8 days)".
    const withRep = side === 'sale' ? repSaleText(v, perText(v), today) : null;
    if (!c) {
      if (side === 'sale' && withRep) {
        return (
          <div className="pc-cell">
            <span className="pc-repchip" title={s.asked ? `Asked ${formatDate(s.asked.at)}${s.asked.by ? ` by ${s.asked.by}` : ''} — Rep Pricing has the send` : ''}>
              <span className="material-symbols-rounded">schedule_send</span>{withRep}
            </span>
            {repFlagChip(v)}
          </div>
        );
      }
      return (
        <div className="pc-cell">
          <span className="pc-val none">{side === 'sale' ? 'No price · not sent' : 'No cost to propose'}</span>
          {side === 'sale' && v.kind !== 'xero_po_line' && (
            <span className="pc-src"><span className="material-symbols-rounded">person</span>Rep: {v.rep?.name || 'none'}</span>
          )}
          {side === 'sale' && s.notForReps && (
            <span className="pc-src pc-not-reps"><span className="material-symbols-rounded">do_not_disturb_on</span>Not for reps{s.notForReps.by ? ` (${s.notForReps.by})` : ''}</span>
          )}
          {side === 'sale' && !readOnly && (
            <span className="pc-src pc-cell-links">
              {sendable(v) && !sendBlock([v]) && (
                <button type="button" className="pc-linkb" onClick={(e) => { e.stopPropagation(); openSend([v]); }}>Send to the rep</button>
              )}
              <button type="button" className="pc-linkb" onClick={(e) => { e.stopPropagation(); if (!isOpen(v.key)) toggleOpen(v.key, e.currentTarget.closest('tr')); }}>Type a price</button>
            </span>
          )}
          {repFlagChip(v)}
        </div>
      );
    }
    const changed = s.proposed && c.id !== s.proposed.id;
    const showWas = !c.isCurrent && num(s.current) > 0 && Math.abs(num(s.current) - num(c.value)) > 0.00005;
    return (
      <div className="pc-cell">
        <span className="pc-val">{money(c.value)}{unitOf(v)}
          {showWas && <span className="pc-was" title={`On the line now: ${money(s.current)}${perText(v)}`}>{money(s.current)}</span>}</span>
        <span className={`pc-src ${c.id === 'manual' ? 'manual' : ''}`}>
          <span className="material-symbols-rounded">{SOURCE_ICONS[c.id] || 'info'}</span>{c.label}{c.detail ? ` · ${c.detail}` : ''}
        </span>
        {side === 'cost' && v.haulage?.per_unit != null && (
          <span className="pc-src"><span className="material-symbols-rounded">local_shipping</span>+ haulage {money(v.haulage.per_unit)}{perText(v)}</span>
        )}
        {changed && <span className="pc-src changed"><span className="material-symbols-rounded">swap_horiz</span>Changed from the proposal</span>}
        {s.reopened && <span className="pc-src warn"><span className="material-symbols-rounded">restart_alt</span>Reopened</span>}
        {side === 'sale' && withRep && c.id === 'rep' && (
          <span className="pc-repchip"><span className="material-symbols-rounded">schedule_send</span>{withRep}</span>
        )}
        {side === 'sale' && s.repHeld && c.id !== 'rep' && (
          <span className="pc-src warn"><span className="material-symbols-rounded">pause_circle</span>Rep price {money(s.repReply.price)}{perText(v)} held</span>
        )}
        {side === 'sale' && repFlagChip(v)}
      </div>
    );
  };

  // One option standing for several sources that give the same figure
  // (1.15.0): each source named, short, the proposed one marked; "Show each"
  // gives each source's own words. The button is inside the option's label, so
  // its click is kept from choosing the option, and Enter / Space from the
  // screen's keys.
  const agreeingText = (en, eachKey, isProposed = () => false, sub = null) => {
    const open = !!eachOpen[eachKey];
    const toggle = (e) => { e.preventDefault(); e.stopPropagation(); setEachOpen(prev => ({ ...prev, [eachKey]: !prev[eachKey] })); };
    return (
      <span className="pc-option-text">
        <span className="l1 pc-agree-sources">
          {en.sources.map((o, i) => (
            <React.Fragment key={o.id}>
              {i > 0 && <span className="pc-agree-sep" aria-hidden="true">·</span>}
              <span className="pc-agree-src">{o.short || o.label}{isProposed(o) && <span className="pc-tag">proposed</span>}</span>
            </React.Fragment>
          ))}
        </span>
        <span className="l2">
          {sub && <>{sub} · </>}
          <span className="pc-agree"><span className="material-symbols-rounded" aria-hidden="true">done_all</span>{en.sources.length} sources agree</span>
          {' · '}
          <button type="button" className="pc-linkb" aria-expanded={open}
            onClick={toggle} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); } }}>
            {open ? 'Hide each' : 'Show each'}</button>
        </span>
        {open && (
          <span className="pc-each">
            {en.sources.map(o => (
              <span key={o.id} className="pc-each-row">
                <span className="pc-each-label">{o.label}{isProposed(o) && <span className="pc-tag">proposed</span>}</span>
                <span className="pc-each-detail">{o.detail || ''}</span>
              </span>
            ))}
          </span>
        )}
      </span>
    );
  };

  const optionsPanel = (v, side) => {
    const s = v[side];
    if (side === 'cost' && v.kind === 'invoice_line' && (s?.infoLines || []).length) {
      return (
        <div className="pc-options">
          <h5><span className="material-symbols-rounded">shopping_cart</span>Costs — for the margin</h5>
          {s.infoLines.map(l => (
            <div key={l.id} className="pc-option info">
              <span className="material-symbols-rounded pc-option-icon">{SOURCE_ICONS[l.id] || 'info'}</span>
              <span className="pc-option-text">
                <span className="l1">{l.label}</span>
                {l.detail && <span className="l2">{l.detail}</span>}
              </span>
              <span className="pc-option-val">{money(l.value)}{unitOf(v)}</span>
            </div>
          ))}
          <p className="pc-muted">{s.infoNote ? `${s.infoNote} ` : ''}A till line's cost is not confirmed here — the till's stock holds it.</p>
        </div>
      );
    }
    if (!s || !s.applies) return <div className="pc-options" />;
    const locked = s.locked || (s.confirmed && !s.reopened);
    const ov = choices[`${v.key}|${side}`];
    const current = s.chosen?.id;
    const name = `${v.key}-${side}`;
    return (
      <div className="pc-options">
        <h5><span className="material-symbols-rounded">{side === 'cost' ? 'shopping_cart' : 'sell'}</span>{side === 'cost' ? 'Cost' : 'Sale price'} options</h5>
        {(s.options || []).length === 0 && <p className="pc-muted">No bill, price change, contract or earlier sale — type a {side === 'cost' ? 'cost' : 'price'} below to set it.</p>}
        {optionEntries(s).map(en => {
          // Options giving the same figure are one (1.15.0): on when the line's
          // source is any of them; choosing it takes the proposal when that is
          // one of them, else the highest in the ladder (en.id).
          const on = en.ids.includes(current);
          return (
            <label key={en.key} className={`pc-option ${on ? 'on' : ''} ${locked && !on ? 'disabled' : ''}${en.merged ? ' pc-option-agree' : ''}`}>
              <input type="radio" name={name} checked={on} disabled={locked || readOnly}
                onChange={(e) => { keepInPlace(e.target); if (en.proposedId) resetChoice(v.key, side); else choose(v.key, side, { id: en.id }); }} />
              {en.merged ? agreeingText(en, `${v.key}|${side}|${en.key}`, (o) => o.id === en.proposedId) : (
                <span className="pc-option-text">
                  <span className="l1">{en.sources[0].label}{en.proposedId && <span className="pc-tag">proposed</span>}</span>
                  {en.sources[0].detail && <span className="l2">{en.sources[0].detail}</span>}
                </span>
              )}
              <span className="pc-option-val">{money(en.value)}{unitOf(v)}</span>
            </label>
          );
        })}
        {!locked && (
          <label className={`pc-option ${current === 'manual' ? 'on' : ''}`}>
            <input type="radio" name={name} checked={current === 'manual'} disabled={readOnly}
              onChange={(e) => { keepInPlace(e.target); choose(v.key, side, { id: 'manual', value: ov?.value ?? '' }); }} />
            <span className="pc-option-text">
              <span className="l1">Type a {side === 'cost' ? 'cost' : 'price'}</span>
              <span className="l2">Sends the line to Check</span>
            </span>
            {(() => {
              // Per the line's unit, per T or per kg (1.11.0, utils/pricePer).
              const opts = perOptions([v]);
              const per = ov?.entered?.per || defaultPer([v], { single: enterPer[`line_${side}`] });
              const text = ov?.id === 'manual' ? String(ov.entered ? ov.entered.value : (ov.value ?? '')) : '';
              const put = (t, p, el) => {
                if (el) keepInPlace(el);
                const lineValue = p === PER_UNIT ? t : (t === '' ? '' : plainFigure(toLineUnit(t, p, lineKgOf(v))));
                choose(v.key, side, { id: 'manual', value: lineValue, entered: { value: t, per: p } });
              };
              return (
                <PerFigureInput text={text} per={per} options={opts} disabled={readOnly} ariaLabel={`Type a ${side}`}
                  eq={per !== PER_UNIT && ov?.id === 'manual' && ov.value !== '' && ov.value != null ? `= ${money(ov.value)}${perText(v)}` : ''}
                  onText={(t, el) => put(t, per, el)}
                  onPer={(p, el) => { setEnterPer(prev => ({ ...prev, [`line_${side}`]: p })); if (text !== '') put(text, p, el); }} />
              );
            })()}
          </label>
        )}
        {side === 'sale' && v.customer?.id && v.item?.code && v.kind !== 'po_line' && (
          <PricingEarlierSales seeCosts={!!data.can.see_costs} fallbackPer={perUnitLabel(v).known ? perText(v) : ''}
            line={{ key: v.key, accountId: v.customer.id, itemCode: v.item.code, orderId: v.order_id || null, orderLineId: v.order_line_id || null }} />
        )}
        {side === 'cost' && v.haulage && (
          <div className="pc-note"><span className="material-symbols-rounded">local_shipping</span>{v.haulage.text || v.haulage.pending}</div>
        )}
        {side === 'cost' && v.till?.cost != null && (
          <div className="pc-note"><span className="material-symbols-rounded">point_of_sale</span>
            Till cost {money(v.till.cost)}{perText(v)} on {formatDate(v.till.day)} (Epos Now price list) — for reference, not offered as the cost.</div>
        )}
        {side === 'sale' && v.kind === 'xero_po_line' && (
          <div className="pc-invoice-to">
            <label>Invoice to</label>
            {v.invoice ? (
              <span>{v.invoice_to?.name || v.customer?.name} — invoiced ({v.invoice.invoice_no})</span>
            ) : customers ? (
              <SearchableDropdown
                options={[
                  ...(v.invoice_to?.how && v.invoice_to.how !== 'chosen' ? [] : [{ value: '', label: '— The customer on the PO —' }]),
                  ...customers.map(c => ({ value: String(c.id), label: `${c.company_name}${c.account_no ? ` (${c.account_no})` : ''}`, searchText: `${c.company_name} ${c.account_no || ''}` }))
                ]}
                value={v.invoice_to?.id ? String(v.invoice_to.id) : ''}
                onChange={(val) => setXeroCustomer(v, val ? Number(val) : null)}
                placeholder="Choose the customer…"
                disabled={readOnly || busy || !data.xero_lines_ready || !data.can.confirm_sales}
              />
            ) : (
              <button type="button" className="pc-chip" onClick={loadCustomers} disabled={readOnly}>
                <span className="material-symbols-rounded">person_search</span>{v.invoice_to?.name || 'Choose the customer'} — change…</button>
            )}
            <span className="pc-muted">
              {v.invoice_to?.how === 'chosen' ? `Chosen here${v.invoice_to.po_name ? ` · the PO says ${v.invoice_to.po_name}` : ''}`
                : v.invoice_to?.how === 'name' ? 'The customer the PO names'
                  : v.invoice_to?.how === 'postcode' ? `Found by the PO's delivery postcode${v.invoice_to.po_name ? ` · the PO says ${v.invoice_to.po_name}` : ''}`
                    : `Not found from the PO${v.invoice_to?.po_name ? ` (it says ${v.invoice_to.po_name})` : ''} — choose who to invoice`}
            </span>
            {!v.invoice && <span className="pc-muted">{v.invoice_block ? `Invoice: ${v.invoice_block}` : 'Ready to invoice'}</span>}
          </div>
        )}
        {side === 'sale' && reviewable(v) && (
          <div className="pc-ask">
            <span><b>{v.rep?.name || 'The rep'} sent back {money(s.repReply.price)}{perText(v)}</b>
              <br /><span className="pc-muted">{s.repReply.status === 'held' ? 'Held' : 'Not reviewed yet'} · {formatDate(s.repReply.date)}{s.repReply.notes ? ` · "${s.repReply.notes}"` : ''}</span></span>
            <span className="pc-ask-buttons">
              <button type="button" className="btn-icon" disabled={busy || !!reviewBlock([v], 'approve')} onClick={() => runReview([v], 'approve')}
                title={reviewBlock([v], 'approve') || `Approve ${money(s.repReply.price)} — confirms the sale price`}>
                <span className="material-symbols-rounded">thumb_up</span>
              </button>
              {s.repReply.status !== 'held' && (
                <button type="button" className="btn-icon" disabled={busy || !!reviewBlock([v], 'hold')} onClick={() => runReview([v], 'hold')}
                  title={reviewBlock([v], 'hold') || 'Hold it — into Held rep prices'}>
                  <span className="material-symbols-rounded">pause_circle</span>
                </button>
              )}
            </span>
          </div>
        )}
        {side === 'sale' && sendable(v) && (
          <div className="pc-ask">
            <span><b>{s.repHeld ? 'Second price check with' : 'Ask'} {v.rep?.name || 'the rep'}</b><br /><span className="pc-muted">Opens the email and spreadsheet to check before it goes (R)</span></span>
            <button type="button" className="btn-icon" onClick={() => openSend([v])} disabled={!!sendBlock([v])}
              title={sendBlock([v]) || 'Review and send to the rep'}>
              <span className="material-symbols-rounded">forward_to_inbox</span>
            </button>
          </div>
        )}
        {s.confirmed && !s.locked && (
          <div className="pc-ask">
            <span className="pc-muted">Confirmed {money(s.confirmed.value)}{s.confirmed.by ? ` by ${s.confirmed.by}` : ''} — undo to put back what the line held before.</span>
            <button type="button" className="btn-icon" disabled={readOnly || busy} onClick={() => unconfirmOne(v, side)} title="Undo this confirmation">
              <span className="material-symbols-rounded">undo</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  const canConfirmSide = (v, side) => {
    const s = v[side];
    if (!s?.applies) return { ok: false, why: side === 'sale' ? (s?.addedTo?.note || 'No sale on this line') : 'No cost to confirm here' };
    if (readOnly) return { ok: false, why: 'View only' };
    if (s.locked) return { ok: false, why: 'Invoiced — the sale price is read only' };
    if (s.confirmed && !s.reopened) return { ok: false, why: 'Already confirmed — expand the row to undo' };
    if (s.block) return { ok: false, why: s.block };
    if (!s.writable) return { ok: false, why: side === 'cost' ? 'Needs permission to see costs' : 'Needs permission to price sales orders' };
    if (!data.ready) return { ok: false, why: 'Not ready — migration pricing_costing.sql has not run' };
    if (!(num(s.chosen?.value) > 0)) return { ok: false, why: `Choose or type a ${side === 'cost' ? 'cost' : 'price'} first` };
    return { ok: true, why: `Confirm ${money(s.chosen.value)}${perText(v)}` };
  };

  // ---- one value for many lines: a group, one line, or the ticked lines (1.6.0) ----
  const lineLabelOf = (v) => [v.refs?.[0]?.label, formatDate(v.delivery_date)].filter(Boolean).join(' · ');
  const sideWord = (side) => (side === 'cost' ? 'cost' : 'sale price');
  const quietWhy = (why) => /^No (cost|sale) on this line$/.test(why);
  // Each line takes the choice as its own (held where it is while edited).
  // `gk` remembers, for the group's panel, the lines left as they were.
  const applySide = (rows, side, choice, gk = null) => {
    // A typed figure per T / kg becomes each line's own unit's (utils/pricePer).
    const { set, skipped } = sideChoicesPer(rows, side, choice);
    set.forEach(x => holdFor(x.key));
    setChoices(prev => {
      const next = { ...prev };
      set.forEach(x => {
        if (x.choice) next[`${x.key}|${side}`] = x.choice;
        else delete next[`${x.key}|${side}`];
      });
      return next;
    });
    if (gk) setGroupSkips(prev => ({ ...prev, [`${gk}|${side}`]: skipped.filter(x => !/already confirmed|Invoiced|^No (cost|sale) on/.test(x.why)) }));
    return { set, skipped };
  };
  const setBlock = (rows, side) => {
    if (readOnly) return 'View only';
    const g = groupSideOptions(rows, side);
    if (g.takers.length) return null;
    const loud = g.blocked.find(b => !quietWhy(b.why));
    if (loud) return loud.why;
    if (side === 'cost' && !data.can.see_costs) return 'Needs permission to see costs';
    return `No ${sideWord(side)} on ${rows.length === 1 ? 'this line' : 'these lines'}`;
  };
  const openSet = (rows, sides, title, gk = null) => {
    const live = sides.filter(side => !setBlock(rows, side));
    if (!live.length) return;
    setSetAsk({ title, keys: rows.map(r => r.key), sides, gk, x: pointRef.current.x, y: pointRef.current.y });
  };
  const confirmBlockFor = (side) => (readOnly ? 'View only'
    : !data.ready ? 'Not ready — migration pricing_costing.sql has not run'
      : side === 'sale' && !data.can.confirm_sales ? 'Needs permission to price sales orders'
        : side === 'cost' && !data.can.see_costs ? 'Needs permission to see costs' : null);
  // The popover's answer: set each side on its lines; "Set and confirm" then
  // confirms them — one request, one Undo — with the lines left out named.
  const applySet = (ask, bySide, confirm) => {
    const rows = ask.keys.map(k => viewByKey.get(k)).filter(Boolean);
    setSetAsk(null);
    const touched = Object.keys(bySide).flatMap(side => rows.map(v => `${v.key}|${side}`));
    const before = Object.fromEntries(touched.filter(k => choices[k]).map(k => [k, choices[k]]));
    const done = {};
    let after = rows;
    Object.entries(bySide).forEach(([side, choice]) => {
      done[side] = applySide(rows, side, choice, ask.gk);
      const by = new Map(done[side].set.map(x => [x.key, x.choice]));
      after = after.map(v => (by.has(v.key) ? withChoice(v, side, by.get(v.key)) : v));
    });
    if (confirm) {
      const items = [];
      const skipped = [];
      after.forEach(v => Object.keys(bySide).forEach(side => {
        if (!v[side]?.applies || (v[side].confirmed && !v[side].reopened)) return;
        const it = itemFor(v, side);
        if (it) items.push(it);
        else skipped.push({ label: `${lineLabelOf(v)} (${side === 'cost' ? 'cost' : 'sale'})`, why: canConfirmSide(v, side).why });
      }));
      runConfirm(items, null, skipped);
      return;
    }
    const said = Object.entries(done).map(([side, d]) => {
      const choice = bySide[side];
      const what = choice.id === 'manual' ? money(choice.value)
        : (groupSideOptions(rows, side).entries.find(en => en.ids.includes(choice.id))?.label || choice.id);
      return `${side === 'cost' ? 'Cost' : 'Sale'} ${what} on ${d.set.length} line${d.set.length === 1 ? '' : 's'}${d.skipped.filter(x => !quietWhy(x.why)).length ? ` (${d.skipped.filter(x => !quietWhy(x.why)).length} left as they were)` : ''}`;
    });
    showToast(`${said.join(' · ')} — not confirmed yet`, false, {
      label: 'Put back',
      run: () => setChoices(prev => {
        const next = { ...prev };
        touched.forEach(k => { if (before[k]) next[k] = before[k]; else delete next[k]; });
        return next;
      })
    });
  };
  // The three "Set …" menu entries for some lines.
  const setActions = (rows, suffix, keyPrefix, gk = null, first = null) => {
    const what = (rows.length === 1 && !gk) || !suffix ? '' : ` ${suffix}`;
    const head = rows.length === 1 && !gk ? `${lineLabelOf(rows[0])} · ${rows[0].item?.name || rows[0].item?.code || ''}`
      : gk ? `${rows[0]?.item?.name || rows[0]?.item?.code || 'This product'} — ${rows[0]?.customer?.name || ''} (${rows.length} lines)`
        : `${rows.length} ticked lines`;
    const cb = setBlock(rows, 'cost');
    const sb = setBlock(rows, 'sale');
    const list = [
      { key: `${keyPrefix}-set-cost`, side: 'cost', icon: 'edit', label: `Set cost${what}…`, disabled: !!cb, title: cb || 'One cost on every line, or each line its own of one kind', onClick: () => openSet(rows, ['cost'], `Set cost — ${head}`, gk) },
      { key: `${keyPrefix}-set-sale`, side: 'sale', icon: 'edit', label: `Set sale${what}…`, disabled: !!sb, title: sb || 'One sale price on every line, or each line its own of one kind', onClick: () => openSet(rows, ['sale'], `Set sale price — ${head}`, gk) },
      { key: `${keyPrefix}-set-both`, side: 'both', icon: 'edit_note', label: `Set both${what}…`, disabled: !!cb && !!sb, title: cb && sb ? cb : (cb || sb ? `Only the ${sideWord(cb ? 'sale' : 'cost')} can be set: ${cb || sb}` : 'The cost and the sale price together'), onClick: () => openSet(rows, ['cost', 'sale'], `Set cost and sale — ${head}`, gk) }
    ];
    // Right-clicked on a cost or sale cell: that side first.
    return first ? [...list.filter(a => a.side === first), ...list.filter(a => a.side !== first)] : list;
  };

  // ---- invoicing from the Confirmed pile (Cam, 19 Oct) ----
  // An order is invoiced whole: every line of it on the screen confirmed.
  const orderRows = useMemo(() => {
    const m = new Map();
    views.forEach(v => { if (v.order_id) m.set(v.order_id, [...(m.get(v.order_id) || []), v]); });
    return m;
  }, [views]);
  // The order's lines with no supplied quantity recorded, by name.
  const unsuppliedOf = (orderId) => (orderRows.get(orderId) || [])
    .filter(x => x.supplied === null || x.supplied === undefined)
    .map(x => x.item?.name || x.item?.code || 'a line');
  const invoiceBlockFor = (v) => {
    if (v.kind !== 'order_line') return 'Only an order is invoiced here';
    // Its tonnage was added to the customer's other order: invoiced there.
    if (v.added_to) return `Added to ${v.added_to.order_number} — invoiced there`;
    if (v.invoice) return `Already invoiced (${v.invoice.invoice_no})`;
    if (v.draft) return `${v.draft.invoice_no} is a draft waiting for approval — approve it instead`;
    if (!v.status?.delivered) return 'Not delivered yet';
    const so = v.refs.find(r => r.type === 'SO-D')?.label || 'the order';
    const open = (orderRows.get(v.order_id) || []).filter(x => x.pile !== 'done');
    if (open.length) {
      return open.some(x => x.key === v.key)
        ? 'Confirm its cost and sale price first'
        : `Confirm the other line${open.length === 1 ? '' : 's'} of ${so} first (${open.length})`;
    }
    // Invoiced on what was supplied, never the ordered quantity (Cam, 2 Nov 2026).
    const unsupplied = unsuppliedOf(v.order_id);
    if (unsupplied.length) {
      return `No supplied quantity on ${unsupplied.join(', ')} yet — record it first (Add Supplied Qty on ${so}), then invoice it`;
    }
    if (readOnly) return 'View only';
    if (!data.can.approve_invoices) return "Invoicing needs the 'Create invoices' permission";
    if (!data.ready) return 'Not ready — migration pricing_costing.sql has not run';
    return null;
  };
  const invoiceableOrders = (rows) => {
    const ids = new Map();
    rows.forEach(v => { if (!invoiceBlockFor(v)) ids.set(v.order_id, v.refs.find(r => r.type === 'SO-D')?.label || `order ${v.order_id}`); });
    return ids;
  };
  const askInvoice = (rows) => {
    const orders = invoiceableOrders(rows);
    // Ticked orders held back only for want of a supplied quantity: named, so
    // the person knows to record them (Cam, 2 Nov 2026 — never invoiced on
    // the ordered quantity).
    const waiting = new Map();
    rows.forEach(v => {
      if (v.kind !== 'order_line' || orders.has(v.order_id) || waiting.has(v.order_id)) return;
      if (v.invoice || v.draft || v.added_to || !v.status?.delivered) return;
      const missing = unsuppliedOf(v.order_id);
      if (missing.length) waiting.set(v.order_id, `${v.refs.find(r => r.type === 'SO-D')?.label || `order ${v.order_id}`} (${missing.join(', ')})`);
    });
    const waitingText = waiting.size
      ? `No supplied quantity recorded yet, so not invoiced: ${[...waiting.values()].slice(0, 10).join('; ')}`
        + `${waiting.size > 10 ? ` and ${waiting.size - 10} more` : ''}. Record it with Add Supplied Qty on the order, then invoice it.`
      : '';
    if (!orders.size) {
      setAlert({
        isOpen: true, type: 'info',
        message: waitingText || 'None of these can be invoiced yet: an order is invoiced once it is delivered, every line of it is confirmed and every line has its supplied quantity recorded.'
      });
      return;
    }
    const names = [...orders.values()];
    const list = `${names.slice(0, 12).join(', ')}${names.length > 12 ? ` and ${names.length - 12} more` : ''}`;
    const all = [...orders.keys()];
    const base = `Raise ${orders.size === 1 ? 'an invoice' : `${orders.size} invoices`} for ${list} at the confirmed prices and supplied quantities? `
      + `${orders.size === 1 ? 'It goes' : 'Each goes'} to Xero as an approved invoice.`;
    // Each order's delivery date (the window's default invoice date and the
    // note's "{delivery date}").
    const delivered = new Map();
    rows.forEach(v => { if (orders.has(v.order_id) && !delivered.has(v.order_id)) delivered.set(v.order_id, v.delivery_date || null); });
    setInvoiceAsk({
      orders: all.map(id => ({ id, number: orders.get(id), deliveryDate: delivered.get(id) || null })),
      message: waitingText ? `${base}\n\n${waitingText}` : base,
      confirmText: orders.size === 1 ? 'Invoice' : `Invoice ${orders.size}`
    });
  };
  const runInvoice = async (orderIds, bookOver = null) => {
    setBusy(true);
    try {
      // Any number of orders: the server raises them and sends them to Xero
      // one at a time, waiting out "Xero is busy", while the window shows it.
      const out = await invoicePriorMonth.run((extra) => fetch(`${API_URL}/api/pricing-costing/invoice/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ orderIds, ...(bookOver ? { bookOver } : {}), ...extra })
      }));
      if (out.declined) return;
      const { res, data: body } = out;
      if (res.status === 409 && body?.code === 'INVOICING_RUNNING' && body?.jobId) {
        setInvoiceJob({ id: body.jobId, start: null });
        return;
      }
      if (!res.ok || !body?.success) throw new Error(body?.error || 'Nothing was invoiced');
      if (body.jobId) {
        setInvoiceJob({ id: body.jobId, start: { total: body.total, results: body.results || [] } });
        return;
      }
      const bad = body.results || [];
      setAlert({
        isOpen: true, type: 'warning',
        message: ['Nothing was invoiced.', '', ...bad.map(b => `• ${b.message}`)].join('\n')
      });
    } catch (err) {
      setAlert({ isOpen: true, type: 'error', message: err.message });
    } finally {
      setBusy(false);
    }
  };
  const editInvoiceBlock = (v) => (!v.invoice ? 'Not invoiced'
    : readOnly ? 'View only'
      : typeof onNavigate !== 'function' ? 'Open it from Search Invoices to edit it'
        : v.invoice.edit_block || null);
  const editInvoice = (v) => startEdit('invoice', v.invoice.id, onNavigate);
  // Book over an invoice already raised (1.20.1): the window says why one can't be.
  const bookOverInvoiceBlock = (v) => (!v.invoice ? 'Not invoiced'
    : readOnly ? 'View only'
      : v.invoice.whole_po ? 'Invoiced in Xero for the whole PO — book it over in Xero'
        : null);

  // ---- actions: the ⋮ menu and the right-click menu share one builder ----
  const rowActions = (v) => {
    const cc = canConfirmSide(v, 'cost');
    const sc = canConfirmSide(v, 'sale');
    const both = [cc, sc].filter(x => x.ok).length;
    const approveBlock = !v.draft ? 'No draft invoice on this line'
      : readOnly ? 'View only'
        : !data.can.approve_invoices ? "Approving needs the 'Create invoices' permission" : null;
    const so = v.refs.find(r => r.type === 'SO-D');
    const po = v.refs.find(r => (r.type === 'PO-D' || r.type === 'PO') && r.xero_id);
    const cb = contractBlock([v]);
    const contract = {
      key: 'contract', icon: 'contract_edit', label: contractLabel(1), disabled: !!cb || busy,
      title: cb || 'Make this price a contract price for the customer — the way the rep-reply import does', onClick: () => openContract([v])
    };
    const notNow = asksContract(v)
      ? [{ key: 'contract-not-now', icon: 'block', label: 'Not now — the rep\'s contract request', disabled: !!notNowBlock([v]) || busy,
        title: notNowBlock([v]) || repContractRequestText(v.rep_contract_request, { action: false }), onClick: () => runNotNow([v]) }]
      : [];
    return [
      // A rep asked for a contract on this line: that comes first (N4).
      ...(asksContract(v) ? [contract, ...notNow] : []),
      { key: 'confirm-cost', icon: 'shopping_cart_checkout', label: 'Confirm cost', disabled: !cc.ok || busy, title: cc.why, onClick: () => runConfirm([itemFor(v, 'cost')].filter(Boolean)) },
      { key: 'confirm-sale', icon: 'sell', label: 'Confirm sale price', disabled: !sc.ok || busy, title: sc.why, onClick: () => runConfirm([itemFor(v, 'sale')].filter(Boolean)) },
      { key: 'confirm-both', icon: 'done_all', label: 'Confirm both', disabled: both < 2 || busy, title: both < 2 ? 'Both sides must be ready to confirm' : 'Confirm the cost and the sale price (Enter)', onClick: () => confirmRows([v], ['cost', 'sale']) },
      ...setActions([v], '', 'line'),
      {
        key: 'send', icon: 'forward_to_inbox', label: v.sale?.repHeld ? 'Send for a second check…' : 'Send to rep…',
        disabled: !!sendBlock([v]), title: sendBlock([v]) || 'Check the email and spreadsheet, then send (R)', onClick: () => openSend([v])
      },
      v.sale?.notForReps
        ? { key: 'rep-back', icon: 'add_circle', label: 'Put back in Send to reps', disabled: busy || !!exclusionBlock([v], false), title: exclusionBlock([v], false) || 'Ask a rep about it again', onClick: () => runExclusion([v], false) }
        : { key: 'rep-out', icon: 'do_not_disturb_on', label: 'Remove from Send to reps', disabled: busy || !!exclusionBlock([v], true), title: exclusionBlock([v], true) || 'Price it without asking a rep — it leaves the Send to reps view', onClick: () => runExclusion([v], true) },
      {
        key: 'rep-approve', icon: 'thumb_up', label: reviewable(v) ? `Approve rep price ${money(v.sale.repReply.price)}` : 'Approve rep price',
        disabled: busy || !!reviewBlock([v], 'approve'), title: reviewBlock([v], 'approve') || 'Confirms the sale price at the rep\'s figure', onClick: () => runReview([v], 'approve')
      },
      {
        key: 'rep-hold', icon: 'pause_circle', label: 'Hold rep price',
        disabled: busy || !!reviewBlock([v], 'hold') || v.sale?.repReply?.status === 'held',
        title: v.sale?.repReply?.status === 'held' ? 'Already held' : (reviewBlock([v], 'hold') || 'Into Held rep prices'), onClick: () => runReview([v], 'hold')
      },
      (() => {
        if (v.kind === 'xero_po_line') {
          const xb = xeroInvoiceBlock([v]);
          return { key: 'invoice', icon: 'request_quote', label: 'Invoice this line…', disabled: !!xb || busy, title: xb || v.invoice_block || 'Choose the customer and raise the invoice', onClick: () => openXeroInvoice([v]) };
        }
        const ib = invoiceBlockFor(v);
        const so = v.refs.find(r => r.type === 'SO-D')?.label;
        return { key: 'invoice', icon: 'request_quote', label: so ? `Invoice ${so}` : 'Invoice', disabled: !!ib || busy, title: ib || 'Raise the invoice for the whole order at the confirmed prices', onClick: () => askInvoice([v]) };
      })(),
      (() => {
        const eb = editInvoiceBlock(v);
        return { key: 'edit-invoice', icon: 'edit_document', label: v.invoice ? `Edit ${v.invoice.invoice_no}` : 'Edit invoice', disabled: !!eb, title: eb || `Open ${v.invoice?.invoice_no} in Create Invoice`, onClick: () => editInvoice(v) };
      })(),
      (() => {
        const bb = bookOverInvoiceBlock(v);
        return { key: 'book-over-invoice', icon: 'event_repeat', label: v.invoice ? `Book over ${v.invoice.invoice_no}…` : 'Book over invoice…', disabled: !!bb, title: bb || 'Move the invoice to another date, or split it over several — while it is unpaid', onClick: () => setBookingOverId(v.invoice.id) };
      })(),
      {
        key: 'approve', icon: 'task_alt', label: v.draft ? `Approve ${v.draft.invoice_no}` : 'Approve invoice',
        disabled: !!approveBlock || busy, title: approveBlock || `Approve ${v.draft?.invoice_no}`, onClick: () => askApprove([v.draft.id])
      },
      {
        key: 'history', icon: 'timeline', label: '12 months for this customer', readSafe: true,
        disabled: !v.customer?.id || !v.item?.code, title: v.customer?.id && v.item?.code ? 'Cost, sale and margin for this customer and product' : 'No customer or item code',
        onClick: () => setHistoryFor({ accountId: v.customer.id, itemCode: v.item.code, title: `${v.customer.name} — ${v.item.code} ${v.item.name || ''}` })
      },
      ...(asksContract(v) ? [] : [contract]),
      { key: 'expand', icon: isOpen(v.key) ? 'unfold_less' : 'unfold_more', label: isOpen(v.key) ? 'Collapse' : 'Expand options', readSafe: true, onClick: () => toggleOpen(v.key) },
      { key: 'order', icon: 'visibility', label: so ? `Open ${so.label}` : 'Open order', readSafe: true, disabled: !so, title: so ? '' : 'Not an order line', onClick: () => so && openRef(so) },
      { key: 'po', icon: 'shopping_cart', label: po ? `Open ${po.label}` : 'Open PO', readSafe: true, disabled: !po, title: po ? '' : 'Bought from stock — no PO', onClick: () => po && openRef(po) }
    ];
  };

  const bulkActions = (ids) => {
    const rows = views.filter(v => ids.has(v.key));
    const drafts = [...new Set(rows.filter(v => v.draft).map(v => v.draft.id))];
    const sendSome = rows.filter(sendable);
    const reviewSome = rows.filter(reviewable);
    return [
      { key: 'b-cost', icon: 'shopping_cart_checkout', label: `Confirm costs (${rows.length} line${rows.length === 1 ? '' : 's'})`, disabled: busy || readOnly, onClick: () => confirmRows(rows, ['cost']) },
      { key: 'b-sale', icon: 'sell', label: `Confirm prices (${rows.length} line${rows.length === 1 ? '' : 's'})`, disabled: busy || readOnly, onClick: () => confirmRows(rows, ['sale']) },
      { key: 'b-both', icon: 'done_all', label: `Confirm both (${rows.length} line${rows.length === 1 ? '' : 's'})`, disabled: busy || readOnly, onClick: () => confirmRows(rows, ['cost', 'sale']) },
      ...setActions(rows, `for the ${rows.length} ticked`, 'b'),
      (() => {
        const n = rows.filter(v => v.kind === 'order_line').length;
        const cb = contractBlock(rows);
        return { key: 'b-contract', icon: 'contract_edit', label: contractLabel(n), disabled: !!cb || busy, title: cb || 'One contract per customer and product — several products of one customer can be one contract', onClick: () => openContract(rows) };
      })(),
      ...(rows.some(asksContract) ? [{
        key: 'b-contract-not-now', icon: 'block', label: `Not now — rep contract requests (${rows.filter(asksContract).length})`,
        disabled: !!notNowBlock(rows) || busy, title: notNowBlock(rows) || '', onClick: () => runNotNow(rows)
      }] : []),
      {
        key: 'b-send', icon: 'forward_to_inbox', label: `Review & send to reps (${sendSome.length})`, disabled: !!sendBlock(rows),
        title: sendBlock(rows) || 'One email per rep, each checked before it goes', onClick: () => openSend(rows)
      },
      {
        key: 'b-rep-out', icon: 'do_not_disturb_on', label: `Remove from Send to reps (${rows.filter(v => sendable(v) && !v.sale?.notForReps).length})`,
        disabled: busy || !!exclusionBlock(rows, true), title: exclusionBlock(rows, true) || '', onClick: () => runExclusion(rows, true)
      },
      {
        key: 'b-rep-back', icon: 'add_circle', label: `Put back in Send to reps (${rows.filter(v => sendable(v) && v.sale?.notForReps).length})`,
        disabled: busy || !!exclusionBlock(rows, false), title: exclusionBlock(rows, false) || '', onClick: () => runExclusion(rows, false)
      },
      {
        key: 'b-xero-invoice', icon: 'request_quote', label: `Invoice Xero PO lines (${rows.filter(v => v.kind === 'xero_po_line' && !v.invoice).length})`,
        disabled: busy || !!xeroInvoiceBlock(rows), title: xeroInvoiceBlock(rows) || 'Each line to its customer — one invoice per customer', onClick: () => openXeroInvoice(rows)
      },
      {
        key: 'b-approve-rep', icon: 'thumb_up', label: `Approve rep prices (${reviewSome.length} line${reviewSome.length === 1 ? '' : 's'})`, disabled: busy || !!reviewBlock(rows, 'approve'),
        title: reviewBlock(rows, 'approve') || '', onClick: () => runReview(rows, 'approve')
      },
      {
        key: 'b-hold-rep', icon: 'pause_circle', label: `Hold rep prices (${reviewSome.length} line${reviewSome.length === 1 ? '' : 's'})`, disabled: busy || !!reviewBlock(rows, 'hold'),
        title: reviewBlock(rows, 'hold') || '', onClick: () => runReview(rows, 'hold')
      },
      (() => {
        const n = invoiceableOrders(rows).size;
        return { key: 'b-invoice', icon: 'request_quote', label: `Invoice ${n} order${n === 1 ? '' : 's'}`, disabled: busy || !n, title: n ? `Raises one invoice per order — ${n} order${n === 1 ? '' : 's'} from these ${rows.length} line${rows.length === 1 ? '' : 's'}` : 'None of these orders is delivered with every line confirmed', onClick: () => askInvoice(rows) };
      })(),
      {
        key: 'b-approve', icon: 'task_alt', label: `Approve ${drafts.length} draft invoice${drafts.length === 1 ? '' : 's'}`, disabled: busy || readOnly || !drafts.length || !data.can.approve_invoices,
        title: !drafts.length ? 'None of these is on a draft invoice' : !data.can.approve_invoices ? "Needs the 'Create invoices' permission" : '',
        onClick: () => askApprove(drafts)
      },
      { key: 'b-untick', icon: 'check_box_outline_blank', label: `Untick these ${rows.length} line${rows.length === 1 ? '' : 's'}`, readSafe: true, onClick: () => setMany([...ids], false) }
    ];
  };

  // Ready lines of the pile in view: Xero PO lines on their own pile, the rest elsewhere.
  // The Ready stage as filtered (the count on its button): Made in Xero on, its lines.
  const readyRows = matching.filter(v => v.stage === 'ready');
  const askConfirmReady = () => setConfirmAsk({
    title: 'Confirm ready lines',
    message: `Confirm the proposed cost and sale price on ${readyRows.length} ready line${readyRows.length === 1 ? '' : 's'}? Each figure is written to its order or PO (a Xero-made PO's cost goes to Xero). Undo puts them back.`,
    confirmText: `Confirm ${readyRows.length}`,
    onConfirm: () => confirmRows(readyRows, ['cost', 'sale'])
  });
  const readyBlock = !data.ready ? 'Not ready — migration pricing_costing.sql has not run' : !readyRows.length ? 'No line is ready' : null;

  const genericActions = () => [
    { key: 'g-ready', icon: 'done_all', label: `Confirm ${readyRows.length} ready line${readyRows.length === 1 ? '' : 's'}`, disabled: readOnly || busy || !!readyBlock, title: readyBlock || '', onClick: askConfirmReady },
    (() => {
      const confirmedRows = matching.filter(v => v.pile === 'done');
      const n = invoiceableOrders(confirmedRows).size;
      return { key: 'g-invoice', icon: 'request_quote', label: `Invoice ${n} confirmed order${n === 1 ? '' : 's'}`, disabled: busy || !n, title: n ? 'Every delivered order whose lines are all confirmed' : 'No confirmed order is ready to invoice', onClick: () => askInvoice(confirmedRows) };
    })(),
    {
      key: 'g-xero-invoice', icon: 'request_quote', label: `Invoice Xero PO lines ready (${xeroReadyRows.length})`,
      disabled: busy || !xeroReadyRows.length || !!xeroInvoiceBlock(xeroReadyRows),
      title: xeroReadyRows.length ? (xeroInvoiceBlock(xeroReadyRows) || 'Every Xero PO line confirmed and delivered') : 'No Xero PO line is ready to invoice',
      onClick: () => openXeroInvoice(xeroReadyRows)
    },
    { key: 'g-import', icon: 'upload_file', label: 'Import rep replies', disabled: readOnly || !data.can.confirm_sales || !data.ready, onClick: () => setImporting(true) },
    { key: 'g-sent', icon: 'outbox', label: 'Sent to reps', readSafe: true, onClick: () => setShowSent(true), title: 'What was sent to which rep and when' },
    { key: 'g-undo', icon: 'undo', label: 'Undo last confirm', disabled: !undoStack.length || busy, title: undoStack.length ? '(U)' : 'Nothing to undo', onClick: undo },
    { key: 'g-all', icon: 'select_all', label: `Tick all ${filtered.length} in this tab`, readSafe: true, onClick: () => setMany(filtered.map(v => v.key), true) },
    { key: 'g-none', icon: 'deselect', label: 'Untick everything', readSafe: true, disabled: !selected.length, onClick: () => setSelected([]) },
    { key: 'g-refresh', icon: 'refresh', label: 'Refresh', readSafe: true, onClick: load },
    { key: 'g-clear', icon: 'filter_alt_off', label: 'Clear filters', readSafe: true, disabled: !filtersApplied, onClick: handleClear }
  ];

  const ctx = useTableContextMenu({ rows: pageRows, selectedIds: selectedSet, getRowId: (r) => r.key });

  // ---- the rows ----
  // The customer as the search tables show one: its account-number badge and
  // name (both open the contact), the first line of its delivery address faded
  // underneath (1.7.0); then whatever follows (the supplier, a PO's own name).
  const openContact = (id) => (e) => { e.stopPropagation(); openEntity('contact', id, { onClose: load }); };
  const customerCell = (customer, after = null, { warn = false } = {}) => (
    <span className="stacked-cell pc-cust">
      <span className="name-with-badge">
        {customer?.account_no && (customer?.id
          ? <button type="button" className="entity-link entity-link-badge account-badge-inline" onClick={openContact(customer.id)}
            title={`Open ${customer.name || 'the contact'}`}>{customer.account_no}</button>
          : <span className="account-badge-inline">{customer.account_no}</span>)}
        {customer?.id
          ? <button type="button" className="entity-link entity-link-inline pc-cust-name" onClick={openContact(customer.id)}
            title={`Open ${customer.name || 'the contact'}`}>{customer.name}</button>
          : <span className={`pc-cust-name${warn ? ' pc-warn-text' : ''}`}>{customer?.name}</span>}
      </span>
      {customer?.delivery_line && <span className="cell-subline pc-cust-addr">{customer.delivery_line}</span>}
      {after}
    </span>
  );
  // The open product's outline (1.9.0): every row of it carries pc-op, its
  // first row pc-op-first and its last pc-op-last (PricingCosting.css draws
  // the outline from these). `op` = null, or { first, last } for this line's
  // place in its product.
  const opClass = (op, first, last) => (op
    ? ` pc-op${op.first && first ? ' pc-op-first' : ''}${op.last && last ? ' pc-op-last' : ''}`
    : '');
  // A line's sale per tonne now (for the group cost panel's margins), or null.
  const salePerTOf = (v) => {
    const val = v.saleValue ?? null;
    if (!(Number(val) > 0)) return null;
    const kg = lineKgOf(v);
    if (kg === null || kg <= 0) return null;
    return fromLineUnit(val, PER_TONNE, kg);
  };
  // P9 (D6): the product group's costs this period — each supplier's latest
  // bill, the highest marked, the customer's usual supplier, and what other
  // customers pay (the office only).
  const groupCostsFor = (rows) => {
    const v = rows.find(r => r.product_group) || null;
    if (!v) return null;
    const prices = rows.map(salePerTOf).filter(x => x !== null);
    const same = prices.length && Math.max(...prices) - Math.min(...prices) < 0.005;
    return (
      <div className="pc-gc-wrap">
        <PricingGroupCosts groupId={v.product_group.id} groupName={v.product_group.name}
          accountId={v.customer?.id || null} accountName={v.customer?.name || null}
          date={v.delivery_date || null} pack={v.product_group.pack || null}
          salePerT={same ? prices[0] : null} seeCosts={!!data.can.see_costs} />
      </div>
    );
  };
  // How a line reaches the customer (P10) — an icon and its words.
  const METHOD_ICONS = { collect: 'person_pin_circle', third_party: 'handshake', direct: 'factory', ours: 'local_shipping' };
  const methodChip = (m) => (m && m.label ? (
    <span className="pc-method" title={m.label}>
      <span className="material-symbols-rounded">{METHOD_ICONS[m.kind] || 'local_shipping'}</span>{m.label}
    </span>
  ) : null);
  // Ordered and supplied, each in its unit (Cam's D7 note: "make clear
  // whether supplied qty shown or if needed").
  const qtyLines = (v) => {
    // None (no weight) is a unit: a count, "1 each" (N6).
    const per = perUnitLabel(v);
    const unitless = !v.unit && !per.known && v.kind !== 'invoice_line' ? ' (no unit)' : '';
    const qtyOf = (qty) => (per.kind === 'none' && !v.unit ? `${formatCount(qty)} each` : qtyText({ qty, unit: v.unit }));
    const ordered = <span className="pc-muted">Ordered {qtyOf(v.qty)}{unitless} · {formatDate(v.delivery_date)}</span>;
    if (v.kind !== 'order_line') return ordered;
    const has = v.supplied !== null && v.supplied !== undefined;
    const due = !!v.status?.delivered || String(v.delivery_date || '') < today;
    return (
      <>
        {ordered}
        {has
          ? <span className="pc-muted pc-supplied">Supplied {qtyOf(v.supplied)}{unitless}</span>
          : <span className={`pc-supplied ${due ? 'pc-supplied-need' : 'pc-muted'}`}
            title="Invoicing uses the supplied quantity; without one the line is invoiced at the ordered quantity (you are asked first)">
            <span className="material-symbols-rounded">{due ? 'pending_actions' : 'schedule'}</span>
            {due ? 'No supplied qty yet — needed before invoicing' : 'Supplied qty after delivery'}
          </span>}
      </>
    );
  };
  // The four-step strip (D2): Cost · Sale · Confirmed · Invoiced.
  const STEP_ICONS = { done: 'check', rep: 'schedule_send', warn: 'priority_high' };
  const stepStrip = (steps, caption) => (
    <div className="pc-steps">
      <div className="pc-steps-dots" role="list" aria-label={steps.map(st => `${st.label}: ${STEP_STATE_WORDS[st.state]}`).join(', ')}>
        {steps.map(st => (
          <span key={st.key} role="listitem" className={`pc-step s-${st.state}`} title={st.why}>
            {st.text ? <span className="pc-step-n">{st.text}</span>
              : <span className="material-symbols-rounded">{STEP_ICONS[st.state] || st.icon}</span>}
          </span>
        ))}
      </div>
      {caption && <span className="pc-steps-cap">{caption}</span>}
    </div>
  );
  const stageCaption = (v) => {
    if (v.place !== v.stage) return null;
    if (v.stage === 'with_reps') return 'sale with the rep';
    if (v.stage === 'needs') return v.cost?.applies && !(v.cost.chosen && Number(v.cost.chosen.value) > 0) && !(v.cost.confirmed && !v.cost.reopened) ? 'needs a cost' : 'needs a price';
    return { check: 'to check', ready: 'ready to confirm', done: v.draft ? 'draft invoice' : 'to invoice', invoiced: 'invoiced' }[v.stage] || '';
  };
  const rowHtml = (v, band, holdKeys = v.key, extra = '', op = null) => {
    const focused = v.key === focusKey;
    const expanded = isOpen(v.key);
    const cls = `${band}${extra} pile-${v.pile} stage-${v.stage}${focused ? ' focus' : ''}${expanded ? ' expanded' : ''}${selectedSet.has(v.key) ? ' ticked' : ''}${moved[v.key] ? ' pc-moved' : ''}`;
    const heldAway = v.place !== v.stage;
    const notes = v.flags.filter(([k]) => k !== 'pile');
    const parts = ['row', ...(notes.length ? ['notes'] : []), ...(expanded ? ['expand'] : [])];
    const opOf = (part) => opClass(op, part === parts[0], part === parts[parts.length - 1]);
    return (
      <React.Fragment key={v.key}>
        <tr data-row={v.key} data-hold={holdKeys} className={`pc-row ${cls}${notes.length || expanded ? ' has-more' : ''}${opOf('row')}`}
          onClick={onRowClick(v)} onDoubleClick={onRowDoubleClick(v)} onContextMenu={ctx.onRowContextMenu(v)}
          title="Click to show the options · double-click to tick">
          <td className="checkbox-col">
            <TickCheckbox id={`pc-${v.key}`} checked={selectedSet.has(v.key)} ariaLabel="Select line"
              onChange={() => {}}
              onMouseDown={drag.onMouseDown(v.key, selectedSet.has(v.key))}
              onMouseEnter={drag.onMouseEnter(v.key)} />
          </td>
          <td>
            <div className="pc-refs">
              {v.refs.map(ref => {
                const can = refOpens(ref);
                return (
                  <button type="button" key={`${ref.type}${ref.label}`} disabled={!can}
                    className={`pc-ref t-${ref.type.toLowerCase().replace(/[^a-z]/g, '')}`}
                    onClick={(e) => { e.stopPropagation(); openRef(ref); }}
                    title={can ? `Open ${ref.label}` : ref.label}>
                    {ref.label}{ref.xero_made ? ' · Xero' : ''}{ref.type === 'DRAFT' ? ' (draft)' : ''}
                  </button>
                );
              })}
            </div>
            {(v.kind === 'order_line' || v.kind === 'xero_po_line') && (
              <div className="pc-status">
                {STATUS_ICONS.map(([k, icon, label]) => (
                  <span key={k} className={`material-symbols-rounded ${v.status?.[k] ? 'on' : ''}`} title={`${label}${v.status?.[k] ? '' : ' — not yet'}`}>{icon}</span>
                ))}
              </div>
            )}
          </td>
          <td>
            <div className="pc-who">
              <span className="pc-prod-name">{v.item?.name || v.item?.code}<UnitFlagsIcon flags={v.unit_flags} /></span>
              <span className="pc-muted">{v.item?.code}{v.additives?.length ? ` + ${v.additives.join(', ')}` : ''}{v.product_group ? ` · group ${v.product_group.name}` : ''}</span>
              {qtyLines(v)}
              {/* A split order's tonnage on this line: "+ 1.94 T from SO-D10421A (split of SO-D10421)". */}
              {(v.tonnage?.added || []).map(a => <span key={a.addition_id} className="pc-muted pc-tonnage-added">{a.text}</span>)}
              <span className="pc-muted pc-route">{methodChip(v.delivery_method)}{v.supplier?.name && <span className="pc-supplier">{v.supplier.name}</span>}</span>
              {v.kind === 'xero_po_line' && v.invoice_to?.po_name && v.invoice_to.po_name !== v.customer?.name && (
                <span className="pc-muted">PO says {v.invoice_to.po_name}</span>
              )}
            </div>
          </td>
          <td>{sideCell(v, 'cost')}</td>
          <td>{sideCell(v, 'sale')}{contractAskBadge([v])}</td>
          <td className="pc-margin">
            {v.margin ? (
              <div className="pc-cell">
                <span className={`pc-val ${v.margin.abs < 0 ? 'neg' : ''}`}>{money(v.margin.abs)}<span className="pc-unit" title={perUnitLabel(v).title}>{perUnitLabel(v).text}</span></span>
                <span className="pc-src">{v.margin.pc.toFixed(1)}% on cost{v.margin.combined ? ' (combined)' : ''}</span>
              </div>
            ) : <span className="pc-na">—</span>}
          </td>
          <td>
            {stepStrip(lineSteps(v, { seeCosts: !!data.can.see_costs, today }), stageCaption(v))}
            {heldAway && <span className="pc-held-note" title="It stays here while you work on it, and moves when you click or tab away">moves when you're done</span>}
          </td>
          <td>
            <div className="actions-cell">
              <button type="button" className="btn-icon" onClick={(e) => { e.stopPropagation(); toggleOpen(v.key, e.currentTarget); }}
                title={expanded ? 'Hide the options' : 'Show the cost and sale options (C)'} aria-expanded={expanded}>
                <span className="material-symbols-rounded">{expanded ? 'expand_less' : 'expand_more'}</span>
              </button>
              <RowActionsMenu actions={rowActions(v)} />
            </div>
          </td>
        </tr>
        {notes.length > 0 && (
          <tr data-hold={holdKeys} className={`pc-notes ${cls}${expanded ? ' has-more' : ''}${opOf('notes')}`} onClick={onRowClick(v)} onDoubleClick={onRowDoubleClick(v)}
            onContextMenu={ctx.onRowContextMenu(v)}>
            <td />
            <td colSpan={7}>
              <div className="pc-notes-in">
                {notes.map(([k, t], i) => (
                  <div key={i} className={`pc-flag ${k}`}><span className="material-symbols-rounded">{k === 'err' ? 'error' : k === 'info' ? 'info' : 'warning'}</span><span>{t}</span></div>
                ))}
              </div>
            </td>
          </tr>
        )}
        {expanded && (
          <tr data-hold={holdKeys} className={`pc-expand ${cls}${opOf('expand')}`} onContextMenu={ctx.onRowContextMenu(v)}
            onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
            <td colSpan={8}>
              <div className="pc-expand-in">
                {optionsPanel(v, 'cost')}
                {optionsPanel(v, 'sale')}
                {groupCostsFor([v])}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // ---- grouped by customer › product (1.5.0; one row per product group, 1.6.0) ----
  let shown = new Set();
  const safeId = (k) => String(k).replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 80);
  // A product group opened is the one open product (1.9.0); closed, its lines
  // close too. `rowEl`, the group's row clicked, stays where it is on screen.
  const toggleGroup = (gk, rowEl = null) => {
    const st = openStateRef.current;
    const product = sections.flatMap(c => c.products).find(p => p.key === gk);
    if (!product) return;
    if (isProductOpen(st, product)) {
      if (product.lines.some(k => holdsRef.current[k])) release();
    } else {
      release(product.lines);
    }
    keepInPlace(rowEl);
    setOpenState(toggleProduct(st, product));
  };
  // Fold / unfold a customer section; folding closes its products.
  const toggleCustomerSection = (ck) => {
    const section = sections.find(c => c.key === ck);
    if (!section) return;
    const st = openStateRef.current;
    if (!isCustomerCollapsed(st, ck)) release();
    setOpenState(toggleCustomer(st, section));
  };
  const onGroupClick = (gk) => (e) => {
    if (ownClick(e)) return;
    if (e.detail > 1) return;
    const rowEl = e.currentTarget;
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => toggleGroup(gk, rowEl), 220);
  };
  const onGroupDoubleClick = (keys) => (e) => {
    if (ownClick(e)) return;
    clearTimeout(clickTimer.current);
    window.getSelection?.().removeAllRanges?.();
    setMany(keys, !keys.every(k => selectedSet.has(k)));
  };
  // Right-click on a group row: its own menu (a cost or sale cell puts that
  // side's "Set …" first). The row stands for the group in the shared menu,
  // marked `menuGroup` — never `group`, which every order line already
  // carries (the server's request keys), so a line read as a group and the
  // menu crashed the screen (Cam, 28 Oct 2026).
  const onGroupContextMenu = (gk, rows) => (e) => {
    const side = e.target?.closest?.('[data-side]')?.getAttribute('data-side') || null;
    ctx.onRowContextMenu({ key: `grp:${gk}`, menuGroup: { gk, keys: rows.map(r => r.key) }, side })(e);
  };
  const countFor = (rows, side) => rows.filter(v => itemFor(v, side)).length;
  const groupActions = (gk, rows, first = null) => {
    const nc = countFor(rows, 'cost');
    const ns = countFor(rows, 'sale');
    const keys = rows.map(r => r.key);
    const openG = !!openGroups[gk];
    return [
      ...setActions(rows, 'for group', 'g', gk, first),
      { key: 'g-split', icon: openG ? 'unfold_less' : 'call_split', label: openG ? 'Hide its lines' : 'Split group (show its lines)', readSafe: true,
        title: openG ? 'Close the group' : 'Show each line, to give some of them their own cost or price', onClick: () => toggleGroup(gk) },
      { key: 'g-cost', icon: 'shopping_cart_checkout', label: `Confirm cost for group (${nc})`, disabled: busy || readOnly || !nc, title: nc ? 'Each line\'s cost, confirmed on its own line' : 'No cost here is ready to confirm', onClick: () => confirmGroup(rows, ['cost']) },
      { key: 'g-sale', icon: 'sell', label: `Confirm sale for group (${ns})`, disabled: busy || readOnly || !ns, title: ns ? 'Each line\'s sale price, confirmed on its own line' : 'No price here is ready to confirm', onClick: () => confirmGroup(rows, ['sale']) },
      { key: 'g-both', icon: 'done_all', label: 'Confirm both for group', disabled: busy || readOnly || !(nc + ns), title: nc + ns ? 'One Undo puts every line back' : 'Nothing here is ready to confirm', onClick: () => confirmGroup(rows, ['cost', 'sale']) },
      { key: 'g-send', icon: 'forward_to_inbox', label: `Send to rep… (${rows.filter(sendable).length})`, disabled: !!sendBlock(rows), title: sendBlock(rows) || 'Asked once — split where the lines\' costs differ', onClick: () => openSend(rows) },
      (() => {
        const cb = contractBlock(rows);
        return { key: 'g-contract', icon: 'contract_edit', label: contractLabel(rows.filter(v => v.kind === 'order_line').length), disabled: !!cb || busy, title: cb || 'Make the group\'s price a contract price for the customer', onClick: () => openContract(rows) };
      })(),
      ...(rows.some(asksContract) ? [{
        key: 'g-contract-not-now', icon: 'block', label: `Not now — rep contract requests (${rows.filter(asksContract).length})`,
        disabled: !!notNowBlock(rows) || busy, title: notNowBlock(rows) || '', onClick: () => runNotNow(rows)
      }] : []),
      (() => {
        // The group's orders, invoiced as a line's own action is — and why not
        // when they can't be (Cam, 3 Nov 2026: a confirmed group said nothing).
        const n = invoiceableOrders(rows).size;
        const why = n ? '' : (rows.map(invoiceBlockFor).find(Boolean) || 'None of these orders can be invoiced yet');
        return { key: 'g-invoice-group', icon: 'request_quote', label: `Invoice ${n} order${n === 1 ? '' : 's'}`, disabled: busy || !n, title: n ? 'One invoice per order, at the confirmed prices' : why, onClick: () => askInvoice(rows) };
      })(),
      { key: 'g-tick', icon: 'check_box', label: `Tick these ${rows.length} lines`, readSafe: true, onClick: () => setMany(keys, true) }
    ];
  };
  // The shared right-click menu: a group row, or a line.
  const menuRowActions = (r) => {
    if (!r?.menuGroup) return rowActions(r);
    const rows = r.menuGroup.keys.map(k => viewByKey.get(k)).filter(Boolean);
    return rows.length ? groupActions(r.menuGroup.gk, rows, r.side) : [];
  };
  const summaryCell = (rows, side) => {
    const sum = sideSummary(rows, side);
    // A group's figures say their unit (D7): the lines' one unit (a request
    // is one unit — a line of unknown unit joins the others').
    const unitRow = rows.find(r => perUnitLabel(r).known) || rows[0];
    if (!sum.applies) {
      if (side === 'sale') return <span className="pc-na">No sale</span>;
      return <span className="pc-na">{data.can.see_costs ? '—' : 'Hidden'}</span>;
    }
    const value = sum.min === null ? null : sum.same ? money(sum.min) : `${money(sum.min)}–${money(sum.max)}`;
    const differs = differsText(sum, side);
    const g = groupSideOptions(rows, side);
    // One source on every open line: say which ("Contract C1", "Typed by hand").
    const source = !differs && g.current ? (g.current === 'manual' ? 'Typed by hand' : g.options.find(o => o.id === g.current)?.label) : null;
    const parts = [
      sum.confirmed ? `${sum.confirmed} of ${sum.applies} confirmed` : null,
      sum.invoiced ? `${sum.invoiced} invoiced` : null,
      !differs && sum.none && value ? `${sum.none} with none` : null
    ].filter(Boolean);
    // Where the group's price is with a rep (C): "With Andy Hawken · 2 of 3 lines · since 20/10 (8 days)".
    const withRep = side === 'sale' ? rows.filter(r => saleWithRep(r)) : [];
    const repWords = (() => {
      if (!withRep.length) return null;
      const first = withRep.map(r => r.sale?.asked?.at).filter(Boolean).sort()[0] || null;
      const names = [...new Set(withRep.map(r => r.sale?.asked?.rep || r.rep?.name).filter(Boolean))];
      const one = repSaleText({ ...withRep[0], sale: { ...withRep[0].sale, asked: withRep[0].sale.asked ? { ...withRep[0].sale.asked, at: first } : null } }, perText(unitRow), today);
      if (withRep.length === 1 && rows.length === 1) return one;
      const since = first ? one.replace(/^.*?( since .*)$/, '$1') : '';
      return `With ${names.join(', ') || 'the rep'} · ${withRep.length} of ${rows.length} lines${since.startsWith(' since') ? ` ·${since}` : ''}`;
    })();
    return (
      <div className="pc-cell">
        {!(repWords && !value) && (
          <span className={`pc-val ${value ? '' : 'none'}`}>{value || (side === 'sale' ? 'No price yet' : 'No cost yet')}{value && unitOf(unitRow)}</span>
        )}
        {repWords && <span className="pc-repchip"><span className="material-symbols-rounded">schedule_send</span>{repWords}</span>}
        {repWords && !value && withRep.length < rows.length && (
          <span className="pc-src"><span className="material-symbols-rounded">outgoing_mail</span>{rows.length - withRep.length} line{rows.length - withRep.length === 1 ? '' : 's'} not sent</span>
        )}
        {differs && <span className="pc-src changed pc-differs"><span className="material-symbols-rounded">call_split</span>{differs}</span>}
        {source && sum.open > 0 && <span className={`pc-src ${g.current === 'manual' ? 'manual' : ''}`}><span className="material-symbols-rounded">{SOURCE_ICONS[g.current] || 'info'}</span>{source}</span>}
        {parts.length > 0 && <span className="pc-src">{parts.join(' · ')}</span>}
      </div>
    );
  };
  // One side's options for every line of the group (the line panel's shape).
  const groupSidePanel = (gk, rows, side) => {
    const g = groupSideOptions(rows, side);
    const sum = sideSummary(rows, side);
    const unitRow = rows.find(r => perUnitLabel(r).known) || rows[0];
    const word = side === 'cost' ? 'cost' : 'sale price';
    const skips = groupSkips[`${gk}|${side}`] || [];
    const lockedPanel = readOnly || !g.takers.length || (side === 'sale' && !data.can.confirm_sales);
    const label = (k) => lineLabelOf(rows.find(r => r.key === k) || {});
    const loud = g.blocked.filter(b => !quietWhy(b.why));
    const differs = differsText(sum, side);
    const name = `grp-${side}-${safeId(gk)}`;
    const anyChoice = g.takers.some(k => choices[`${k}|${side}`]);
    // What one typed figure is per (1.11.0): per tonne for lines in different
    // units (q03), else their own unit — as last switched.
    const takerRows = rows.filter(r => g.takers.includes(r.key));
    const groupMixed = unitsMixed(takerRows);
    const typedOnce = enteredOf(g.takers, side, choices);
    const groupPer = typedOnce?.per || defaultPer(takerRows, { mixed: enterPer[`mixed_${side}`], single: enterPer[`single_${side}`] });
    const groupPerOpts = perOptions(takerRows);
    const groupText = typedOnce ? typedOnce.value : (g.manualValue ?? '');
    const perTonneNote = normalisedText(rows, side, (r) => lineLabelOf(r));
    return (
      <div className="pc-options">
        <h5><span className="material-symbols-rounded">{side === 'cost' ? 'shopping_cart' : 'sell'}</span>
          {side === 'cost' ? 'Cost' : 'Sale price'} for {g.takers.length === rows.length ? `all ${rows.length} lines` : `${g.takers.length} of the ${rows.length} lines`}</h5>
        {!g.takers.length && (
          <p className="pc-muted">{loud.length ? `None of these lines can take a ${word} here.` : side === 'cost' && !data.can.see_costs ? 'Costs are hidden — needs permission to see costs.' : `No ${word} on these lines.`}</p>
        )}
        {g.takers.length > 0 && differs && (
          <p className="pc-muted pc-differs-note"><span className="material-symbols-rounded">call_split</span>{differs} — choose here to set them all the same.</p>
        )}
        {perTonneNote && (
          <p className="pc-muted pc-per-note"><span className="material-symbols-rounded">straighten</span>Per tonne: {perTonneNote}</p>
        )}
        {g.takers.length > 0 && g.entries.map(o => {
          // Options giving the same figure on every line are one (1.15.0).
          const lines = `${o.all ? 'every line' : `${o.lines} of ${g.takers.length} lines`}${o.same ? '' : ' · each line its own'}`;
          return (
            <label key={o.key} className={`pc-option ${g.currentEntry === o.key ? 'on' : ''} ${lockedPanel ? 'disabled' : ''}${o.merged ? ' pc-option-agree' : ''}`}>
              <input type="radio" name={name} checked={g.currentEntry === o.key} disabled={lockedPanel}
                onChange={(e) => { keepInPlace(e.target); applySide(rows, side, entryGroupChoice(o), gk); }} />
              {o.merged ? agreeingText(o, `grp|${gk}|${side}|${o.key}`, () => false, lines) : (
                <span className="pc-option-text">
                  <span className="l1">{o.label}</span>
                  <span className="l2">{lines}</span>
                </span>
              )}
              <span className="pc-option-val">{o.min === null ? '—' : o.same ? money(o.min) : `${money(o.min)}–${money(o.max)}`}{unitOf(unitRow)}</span>
            </label>
          );
        })}
        {g.takers.length > 0 && (
          <label className={`pc-option ${g.current === 'manual' ? 'on' : ''} ${lockedPanel ? 'disabled' : ''}`}>
            <input type="radio" name={name} checked={g.current === 'manual'} disabled={lockedPanel}
              onChange={(e) => { keepInPlace(e.target); applySide(rows, side, { id: 'manual', value: groupText, per: groupPer }, gk); }} />
            <span className="pc-option-text">
              <span className="l1">Type one {side === 'cost' ? 'cost' : 'price'} for them all</span>
              <span className="l2">Sends the lines to Check</span>
            </span>
            <PerFigureInput text={groupText} per={groupPer} options={groupPerOpts} disabled={lockedPanel}
              ariaLabel={`Type one ${word} for these lines`}
              eq={groupPer !== PER_UNIT ? 'each line in its own unit' : ''}
              onText={(t, el) => { keepInPlace(el); applySide(rows, side, { id: 'manual', value: t, per: groupPer }, gk); }}
              onPer={(p, el) => {
                setEnterPer(prev => ({ ...prev, [`${groupMixed ? 'mixed' : 'single'}_${side}`]: p }));
                if (groupText !== '') { keepInPlace(el); applySide(rows, side, { id: 'manual', value: groupText, per: p }, gk); }
              }} />
          </label>
        )}
        {anyChoice && !lockedPanel && (
          <button type="button" className="pc-link pc-reset" onClick={() => {
            g.takers.forEach(k => holdFor(k));
            setChoices(prev => { const n = { ...prev }; g.takers.forEach(k => delete n[`${k}|${side}`]); return n; });
          }}>Back to each line's proposal</button>
        )}
        {loud.length > 0 && (
          <div className="pc-note"><span className="material-symbols-rounded">info</span>
            <span>Not changed here: {loud.slice(0, 6).map(b => `${label(b.key)} — ${b.why}`).join('; ')}{loud.length > 6 ? ` and ${loud.length - 6} more` : ''}</span>
          </div>
        )}
        {skips.length > 0 && (
          <div className="pc-note warn"><span className="material-symbols-rounded">warning</span>
            <span>Left as they were: {skips.slice(0, 6).map(b => `${label(b.key)} — ${b.why}`).join('; ')}{skips.length > 6 ? ` and ${skips.length - 6} more` : ''}</span>
          </div>
        )}
      </div>
    );
  };
  const groupPanel = (gk, rows) => {
    const nc = countFor(rows, 'cost');
    const ns = countFor(rows, 'sale');
    return (
      <div className="pc-expand-in pc-group-panel-in">
        {groupSidePanel(gk, rows, 'cost')}
        {groupSidePanel(gk, rows, 'sale')}
        {rows[0]?.customer?.id && rows[0]?.item?.code && rows[0].kind !== 'po_line' && (
          <div className="pc-gc-wrap">
            <PricingEarlierSales seeCosts={!!data.can.see_costs} fallbackPer={perUnitLabel(rows[0]).known ? perText(rows[0]) : ''}
              line={{ key: rows[0].key, accountId: rows[0].customer.id, itemCode: rows[0].item.code, orderId: rows[0].order_id || null, orderLineId: rows[0].order_line_id || null }} />
          </div>
        )}
        {groupCostsFor(rows)}
        <div className="pc-options pc-group-confirm">
          <h5><span className="material-symbols-rounded">done_all</span>Confirm these {rows.length} lines</h5>
          <div className="pc-group-buttons">
            <button type="button" className="pc-chip" disabled={busy || readOnly || !nc} onClick={() => confirmGroup(rows, ['cost'])}
              title={nc ? 'Each line\'s cost, as shown on its line' : 'No cost here is ready to confirm'}>
              <span className="material-symbols-rounded">shopping_cart_checkout</span>Confirm costs ({nc})</button>
            <button type="button" className="pc-chip" disabled={busy || readOnly || !ns} onClick={() => confirmGroup(rows, ['sale'])}
              title={ns ? 'Each line\'s sale price, as shown on its line' : 'No price here is ready to confirm'}>
              <span className="material-symbols-rounded">sell</span>Confirm prices ({ns})</button>
            <button type="button" className="pc-chip on" disabled={busy || readOnly || !(nc + ns)} onClick={() => confirmGroup(rows, ['cost', 'sale'])}>
              <span className="material-symbols-rounded">done_all</span>Confirm both</button>
            <button type="button" className="pc-chip" disabled={!!sendBlock(rows)} onClick={() => openSend(rows)}
              title={sendBlock(rows) || 'Asked once — split where the lines\' costs differ'}>
              <span className="material-symbols-rounded">forward_to_inbox</span>Send to rep…</button>
          </div>
          <p className="pc-muted">Lines below — change one on its own to split it from the group; set it here to make them all the same. Confirming confirms each line; one Undo puts them all back.</p>
        </div>
      </div>
    );
  };
  // A customer's heading (1.9.0): its chevron folds the section to this row,
  // whose summary reads on its own — products, lines, quantity, sale value, piles.
  const customerHead = (block, continued) => {
    const keys = block.requests.flatMap(r => r.rows.map(x => x.key));
    const all = keys.length > 0 && keys.every(k => selectedSet.has(k));
    const name = block.customer?.name || 'No customer';
    const folded = !!collapsed[block.key];
    const sum = customerSummary(block);
    const qty = sum.quantities.map(([unit, total]) => `${qtyText({ qty: total, unit })}${unit ? '' : ' (no unit)'}`).join(' · ');
    const onHeadClick = (e) => { if (!ownClick(e)) toggleCustomerSection(block.key); };
    return (
      <tr key={`c-${block.key}`} className={`pc-group pc-cust-head${folded ? ' folded' : ''}`} onClick={onHeadClick}
        title={folded ? `Show ${name}'s products` : `Fold ${name} to this row`}>
        <td className="checkbox-col">
          <TickCheckbox id={`pc-cust-${safeId(block.key)}`} checked={all} ariaLabel={`Tick every line for ${name}`}
            title={`Tick all ${keys.length} lines for ${name}`} onChange={(e) => setMany(keys, e.target.checked)} />
        </td>
        <td colSpan={6}>
          <div className="pc-rep-head-in pc-cust-head-in">
            <span className="material-symbols-rounded pc-cust-icon">storefront</span>
            {block.customer?.id || block.customer?.account_no ? customerCell(block.customer) : <span className="pc-cust-name">{name}</span>}
            <span className="pc-cust-sum">
              <span>{sum.products} product{sum.products === 1 ? '' : 's'} · {sum.lines} line{sum.lines === 1 ? '' : 's'}{qty ? ` · ${qty}` : ''}</span>
              {sum.priced > 0 && <span>Sale {money(sum.saleTotal)}{sum.priced < (sum.saleLines ?? sum.lines) ? ` (${sum.priced} of ${sum.saleLines ?? sum.lines} priced)` : ''}</span>}
              {continued && <span>continued</span>}
            </span>
            <span className="pc-cust-piles">
              {stageCountsText(block.requests.flatMap(r => r.rows))}
            </span>
            {(() => {
              const rep = block.requests.flatMap(r => r.rows).find(r => r.rep?.name)?.rep?.name;
              return rep ? <span className="pc-cust-rep">Rep: {rep}</span> : null;
            })()}
          </div>
        </td>
        <td>
          <div className="actions-cell">
            <button type="button" className="btn-icon pc-cust-toggle" onClick={(e) => { e.stopPropagation(); toggleCustomerSection(block.key); }}
              aria-expanded={!folded} aria-label={folded ? `Show ${name}'s products` : `Collapse ${name}`}
              title={folded ? 'Show its products' : 'Collapse to this row (closes its products)'}>
              <span className="material-symbols-rounded">{folded ? 'expand_more' : 'expand_less'}</span>
            </button>
          </div>
        </td>
      </tr>
    );
  };
  // A group's stages in words under its strip: "2 ready · 1 to check".
  const STAGE_SHORT = { needs: 'need a price', with_reps: 'with reps', check: 'to check', ready: 'ready', done: 'confirmed', invoiced: 'invoiced' };
  const stageCountsText = (rows) => {
    const by = new Map();
    rows.forEach(r => by.set(r.stage, (by.get(r.stage) || 0) + 1));
    return STAGE_ORDER.filter(st => by.has(st)).map(st => `${by.get(st)} ${STAGE_SHORT[st]}`).join(' · ');
  };
  const subgroupRows = (request, bandCls) => {
    const rows = request.rows;
    const gk = request.key;
    const keys = rows.map(r => r.key);
    const holdKeys = keys.join(' ');
    const first = rows[0];
    const openG = !!openGroups[gk];
    // Open, the whole product is outlined: this row first, its last line's last row last.
    const op = openG ? ' pc-op' : '';
    const allTicked = keys.every(k => selectedSet.has(k));
    const orders = [...new Map(rows.map(r => r.refs.find(x => x.type === 'SO-D')).filter(Boolean).map(x => [x.label, x])).values()];
    const suppliers = [...new Set(rows.map(r => r.supplier?.name).filter(Boolean))];
    // A line of unknown unit joined the group on the others' one unit (the
    // server's groupKeysForLines), so only the known units count here.
    const units = new Set(rows.map(r => r.unit || '').filter(Boolean));
    const total = rows.reduce((t, r) => t + (Number(r.qty) || 0), 0);
    const days = rows.map(r => r.delivery_date).filter(Boolean).sort();
    const margins = rows.map(r => r.margin?.abs).filter(x => x !== null && x !== undefined);
    const mMin = margins.length ? Math.min(...margins) : null;
    const mMax = margins.length ? Math.max(...margins) : null;
    const notes = flagSummary(rows).filter(f => f.kind !== 'info');
    const out = [];
    out.push(
      <tr key={`g-${gk}`} data-hold={holdKeys} data-customer={first.customer?.name || ''} className={`pc-row pc-sub ${bandCls}${allTicked ? ' ticked' : ''}${openG ? ' expanded' : ''}${notes.length ? ' has-more' : ''}${op}${openG ? ' pc-op-first' : ''}`}
        onClick={onGroupClick(gk)} onDoubleClick={onGroupDoubleClick(keys)} onContextMenu={onGroupContextMenu(gk, rows)}
        title="Click to set the cost and sale for all these lines and see them · double-click to tick them · right-click to set cost / sale for the group">
        <td className="checkbox-col">
          <TickCheckbox id={`pc-grp-${safeId(gk)}`} checked={allTicked} ariaLabel={`Tick the ${rows.length} lines of ${first.item?.name || first.item?.code || 'this product'}`}
            onChange={(e) => setMany(keys, e.target.checked)} />
        </td>
        <td>
          <div className="pc-refs">
            {orders.slice(0, 4).map(ref => (
              <button type="button" key={ref.label} className="pc-ref t-sod" onClick={(e) => { e.stopPropagation(); openRef(ref); }} title={`Open ${ref.label}`}>{ref.label}</button>
            ))}
            {orders.length > 4 && <span className="pc-muted">+{orders.length - 4} more</span>}
          </div>
          <div className="pc-muted pc-sub-count"><span className="material-symbols-rounded">stacks</span>{rows.length} lines<br />{orders.length} order{orders.length === 1 ? '' : 's'}</div>
        </td>
        <td>
          <div className="pc-who">
            <span className="pc-prod-name">{first.item?.name || first.item?.code}
              <UnitFlagsIcon flags={rows.flatMap(r => r.unit_flags || [])}
                prefix={`${rows.filter(r => (r.unit_flags || []).length).length} of these ${rows.length} lines:`} /></span>
            <span className="pc-muted">{first.item?.code}{first.additives?.length ? ` + ${first.additives.join(', ')}` : ''}{first.product_group ? ` · group ${first.product_group.name}` : ''}</span>
            <span className="pc-muted">Ordered {groupQtyText(rows, qtyText) || (units.size === 1 ? qtyText({ qty: total, unit: [...units][0] }) : `${formatQuantity(total)} (no unit)`)} in total · {deliveryDatesText(days)}</span>
            {(() => {
              // Supplied quantities (D7 note): how many lines have one, and whether any is still needed.
              const ol = rows.filter(r => r.kind === 'order_line');
              if (!ol.length) return null;
              const withQty = ol.filter(r => r.supplied !== null && r.supplied !== undefined);
              const due = ol.filter(r => !(r.supplied !== null && r.supplied !== undefined) && (!!r.status?.delivered || String(r.delivery_date || '') < today));
              if (withQty.length === ol.length) {
                const sumSup = withQty.reduce((t, r) => t + (Number(r.supplied) || 0), 0);
                return <span className="pc-muted pc-supplied">Supplied {units.size === 1 ? qtyText({ qty: sumSup, unit: [...units][0] }) : `on all ${ol.length} lines`}</span>;
              }
              return (
                <span className={`pc-supplied ${due.length ? 'pc-supplied-need' : 'pc-muted'}`}>
                  <span className="material-symbols-rounded">{due.length ? 'pending_actions' : 'schedule'}</span>
                  {withQty.length ? `Supplied on ${withQty.length} of ${ol.length} lines` : 'No supplied qty yet'}{due.length ? ` — ${due.length} needed before invoicing` : ''}
                </span>
              );
            })()}
            <span className="pc-muted pc-route">{methodChip(first.delivery_method)}{suppliers.length > 0 && <span className="pc-supplier">{suppliers.join(', ')}</span>}</span>
          </div>
        </td>
        <td data-side="cost" title="Right-click to set the cost for the whole group">{summaryCell(rows, 'cost')}</td>
        <td data-side="sale" title="Right-click to set the sale price for the whole group">{summaryCell(rows, 'sale')}{contractAskBadge(rows)}</td>
        <td className="pc-margin">
          {mMin !== null ? (
            <div className="pc-cell">
              <span className={`pc-val ${mMin < 0 ? 'neg' : ''}`}>{mMax - mMin < 0.00005 ? money(mMin) : `${money(mMin)}–${money(mMax)}`}{unitOf(rows.find(r => perUnitLabel(r).known) || first)}</span>
            </div>
          ) : <span className="pc-na">—</span>}
        </td>
        <td>
          {stepStrip(groupSteps(rows, { seeCosts: !!data.can.see_costs, today }), stageCountsText(rows))}
        </td>
        <td>
          <div className="actions-cell">
            <button type="button" className="btn-icon" onClick={(e) => { e.stopPropagation(); toggleGroup(gk, e.currentTarget); }}
              title={openG ? 'Hide the lines' : 'The cost and sale for them all, and the lines'} aria-expanded={openG} aria-label={openG ? 'Hide the lines' : 'Show the lines'}>
              <span className="material-symbols-rounded">{openG ? 'expand_less' : 'expand_more'}</span>
            </button>
            <RowActionsMenu actions={groupActions(gk, rows)} />
          </div>
        </td>
      </tr>
    );
    if (notes.length) {
      out.push(
        <tr key={`gn-${gk}`} data-hold={holdKeys} className={`pc-notes ${bandCls}${openG ? ' has-more' : ''}${op}`} onClick={onGroupClick(gk)} onDoubleClick={onGroupDoubleClick(keys)}
          onContextMenu={onGroupContextMenu(gk, rows)}>
          <td />
          <td colSpan={7}>
            <div className="pc-notes-in">
              {notes.slice(0, 6).map(f => (
                <div key={f.text} className={`pc-flag ${f.kind}`}><span className="material-symbols-rounded">{f.kind === 'err' ? 'error' : 'warning'}</span><span>{f.text}{f.n > 1 ? ` (${f.n} lines)` : ''}</span></div>
              ))}
            </div>
          </td>
        </tr>
      );
    }
    if (openG) {
      out.push(
        <tr key={`gp-${gk}`} data-hold={holdKeys} className={`pc-expand pc-group-panel ${bandCls}${op}`}
          onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()} onContextMenu={onGroupContextMenu(gk, rows)}>
          <td colSpan={8}>{groupPanel(gk, rows)}</td>
        </tr>
      );
    }
    // The group's own rows are one hover unit (a <tbody>), each line another.
    const bodies = [<tbody key={`gu-${gk}`} className="pc-unit">{out}</tbody>];
    if (openG) {
      rows.forEach((v, i) => {
        shown.add(v.key);
        bodies.push(<tbody key={`lu-${v.key}`} className="pc-unit">{rowHtml(v, bandCls, holdKeys, ' pc-in-group', { first: false, last: i === rows.length - 1 })}</tbody>);
      });
    }
    return bodies;
  };
  // Each hover unit is its own <tbody> (1.9.0): a heading, a product row
  // with its notes and panel, a line with its notes and options.
  const groupedBody = (out) => {
    shown = new Set();
    let lastBlock = null;
    let gband = 0;
    let customers = 0;
    pageEntries.forEach(({ block, request }) => {
      if (block !== lastBlock) {
        lastBlock = block;
        gband = 0;
        out.push(
          <tbody key={`ch-${block.key}`} className="pc-head-unit">
            {customers > 0 && <tr className="pc-cust-gap" aria-hidden="true"><td colSpan={8} /></tr>}
            {customerHead(block, request !== block.requests[0])}
          </tbody>
        );
        customers += 1;
      }
      // A folded customer shows only its heading.
      if (collapsed[block.key]) return;
      const bandCls = gband % 2 ? 'pc-band-even' : 'pc-band-odd';
      if (request.rows.length === 1) {
        const v = request.rows[0];
        shown.add(v.key);
        out.push(<tbody key={`lu-${v.key}`} className="pc-unit">{rowHtml(v, bandCls, v.key, '', isOpen(v.key) ? { first: true, last: true } : null)}</tbody>);
      } else {
        out.push(...subgroupRows(request, bandCls));
      }
      gband += 1;
    });
  };

  const body = [];
  if (pageEntries) groupedBody(body);
  shownRef.current = pageEntries ? shown : null;

  const selectedDrafts = [...new Set(selectedRows.filter(v => v.draft).map(v => v.draft.id))];

  // The header's "More" menu: what used to be round icons, with their words.
  const moreActions = [
    { key: 'm-import', icon: 'upload_file', label: 'Import rep replies…', disabled: readOnly || !data.can.confirm_sales || !data.ready,
      title: !data.ready ? 'Not ready — migration pricing_costing.sql has not run' : 'Read a rep\'s filled-in price request spreadsheet', onClick: () => setImporting(true) },
    { key: 'm-sent', icon: 'outbox', label: 'Sent to reps', readSafe: true,
      title: 'What was sent to which rep and when — download the spreadsheet as sent, resend it, or ask again with today\'s figures', onClick: () => setShowSent(true) },
    { key: 'm-refresh', icon: 'refresh', label: 'Refresh', readSafe: true, onClick: load }
  ];
  const rangeWords = `${formatDate(shownRange.from)} – ${formatDate(shownRange.to)}`;
  const variationBlock = hasPermission(user, 'variation_review.use') ? null : "Variation Review needs its permission ('Variation Review')";
  const helpText = 'Check what the system proposes and confirm it. Click a row to see its cost and sale options; double-click or the tick box ticks it; right-click for actions. '
    + 'Keys: J / K move, Enter confirm, C open, R send to the rep, U undo, X tick. Nothing is written until you confirm. '
    + 'The circles on each line: Cost · Sale · Confirmed · Invoiced — green done, blue with a rep, amber needs a look, grey not yet.';
  // ===== rep-pricing-ui (D3, Cam 28 Oct 2026) — "Lines | Rep Pricing". Keep whole. =====
  // The switch counts the requests due to go to reps from these lines (the
  // filters applied); Rep Pricing is shown instead of the lines, with the same
  // filters, so the dates carry over. After every hook above.
  const repSwitch = useRepPricingSwitch(matching);
  if (repSwitch.view === 'reps') {
    // The range is the person's remembered one (pc-lines): Rep Pricing reads and
    // sets it through the shared filters, with the same period choices.
    return <RepPricingView filters={sharedFilters} setFilters={setSharedFilters} onView={repSwitch.setView} clearedFilters={CLEARED_FILTERS}
      rangePresets={rangeChoices} />;
  }
  // ===== end rep-pricing-ui =====

  return (
    <div className="search-customer-orders pricing-costing" onKeyDown={onKeyDown} tabIndex={-1} ref={rootRef}>
      <div className="pc-head">
        <div className="workflow-info pc-head-title">
          <h1>Pricing &amp; Costing</h1>
          <span className="info">The proposed cost and sale price of every line delivering {rangeWords}</span>
          <span className="material-symbols-rounded pc-help" title={helpText} aria-label={helpText} tabIndex={0}>help</span>
        </div>
        {/* Clear Filters, left of the view switch (Cam, 29 Oct 2026). */}
        <div className="buttons pc-clear-filters">
          <div>
            <button type="button" className={clearFiltersClass(filtersApplied)} onClick={handleClear}
              title={filtersApplied ? 'Search, rep, product, Made in Xero and Only lines back to all' : 'No filters applied'}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
              <span className="btn-label">Clear Filters</span>
            </button>
          </div>
        </div>
        {repSwitch.element /* rep-pricing-ui: "Lines | Rep Pricing" */}
        <div className="buttons pc-header-buttons">
          <div><RowActionsMenu actions={moreActions} triggerIcon="more_horiz" triggerTitle="More — import rep replies, what was sent to reps, refresh" /></div>
          <div>
            <button type="button" className="btn-slideout pc-solid pc-solid-primary" onClick={() => setVariationOpen(true)}
              disabled={!!variationBlock} data-testid="variation-review-button"
              title={variationBlock || `Variation Review — every customer and product invoiced at more than one price (${rangeWords})`}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">show_chart</span></div>
              <span className="btn-label">Variation Review</span>
            </button>
          </div>
          <div>
            <button type="button" className="btn-slideout pc-solid pc-solid-error" onClick={undo} disabled={!undoStack.length || busy}
              title={undoStack.length ? 'Undo the last confirm (U)' : 'Nothing to undo'}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">undo</span></div>
              <span className="btn-label">Undo</span>
            </button>
          </div>
          <div>
            <button type="button" className="btn-slideout pc-solid pc-solid-success pc-primary" disabled={readOnly || busy || !!readyBlock}
              title={readyBlock || `Confirm the proposed cost and sale price on the ${readyRows.length} ready line${readyRows.length === 1 ? '' : 's'}`}
              onClick={askConfirmReady}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">done_all</span></div>
              <span className="btn-label">Confirm {readyRows.length} ready line{readyRows.length === 1 ? '' : 's'}</span>
            </button>
          </div>
        </div>
      </div>

      {!data.ready && data.loaded && (
        <div className="pc-banner warn">
          <span className="material-symbols-rounded">construction</span>
          Not ready — migration pricing_costing.sql has not run. The proposals are shown; nothing can be confirmed yet.
        </div>
      )}

      <div className="search-filters pc-filters">
        <div className="filter-group pc-f-search">
          <label>Search
            <SearchHelp fields={['order / PO / invoice no.', 'customer', 'account no.', 'supplier', 'item code', 'product', 'additives', 'rep']} />
          </label>
          <Input type="text" placeholder="Customer, product, order or PO…" value={filters.search || ''} icon="search"
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))} />
        </div>
        {/* Delivering From · To · the months under the arrow — laid out as
            Search Customer Orders' (Cam, 29 Oct 2026); .filter-row brings
            DateRangeFilter.css's joins. */}
        <div className="filter-row pc-f-range" title={rangeWhy || 'Delivery dates — type them, or choose a month from the arrow'}>
          <DateRangeFilter from={shownRange.from} to={shownRange.to} preset={rangeDraft ? 'custom' : range.preset}
            presets={rangeChoices}
            onChange={setRange} />
        </div>
        <div className="filter-group pc-f-rep">
          <label>Rep</label>
          <MultiSelectDropdown
            options={repOptions}
            value={filters.reps || []}
            onChange={(vals) => setFilters(prev => ({ ...prev, reps: vals }))}
            placeholder="All reps"
            tickBoxes
            hideTags
            searchable
            summarize={(vals) => (vals.length === 0 ? 'All reps'
              : vals.length === 1 ? (vals[0] === NO_REP ? 'No rep' : vals[0]) : `${vals.length} reps`)}
          />
        </div>
        <div className="filter-group pc-f-product">
          <label>Product</label>
          <MultiSelectDropdown
            options={PRODUCT_CATEGORIES.map(c => ({ value: c.key, label: c.label }))}
            value={filters.categories || []}
            onChange={(vals) => setFilters(prev => ({ ...prev, categories: vals }))}
            placeholder="All products"
            tickBoxes
            hideTags
            summarize={(vals) => (vals.length === 0 ? 'All products'
              : vals.length === 1 ? (PRODUCT_CATEGORIES.find(c => c.key === vals[0])?.label || '1 category') : `${vals.length} categories`)}
          />
        </div>
        <div className="filter-group pc-f-only">
          <label>Only lines</label>
          <MultiSelectDropdown
            options={STATUS_FILTERS.map(sf => ({ value: sf.key, label: sf.label }))}
            value={filters.status || []}
            onChange={(vals) => setFilters(prev => ({ ...prev, status: vals }))}
            placeholder="Any"
            tickBoxes
            hideTags
            summarize={(vals) => (vals.length
              ? STATUS_FILTERS.filter(sf => vals.includes(sf.key)).map(sf => sf.label).join(', ')
              : 'Any')}
          />
        </div>
        <div className="filter-group pc-f-xero" title="Lines of purchase orders made in Xero (PO-…) with no order of ours — priced here and invoiced per line. Off: every other line.">
          <label htmlFor="pc-made-in-xero">Made in Xero</label>
          <div className="checkbox-container">
            <div className="check light">
              <input id="pc-made-in-xero" type="checkbox" checked={!!filters.xero}
                onChange={(e) => setFilters(prev => ({ ...prev, xero: e.target.checked }))} />
              <label htmlFor="pc-made-in-xero"></label>
            </div>
          </div>
        </div>
        <div className="filter-group pc-f-page">
          <label>Per page</label>
          <Dropdown value={String(pageSize)} onChange={(val) => { setPageSize(val); setPage(1); }}
            options={[{ value: '50', label: '50' }, { value: '100', label: '100' }, { value: '200', label: '200' }, { value: '500', label: '500' }]} />
        </div>
      </div>
      {rangeWhy && <div className="pc-banner warn"><span className="material-symbols-rounded">date_range</span>{rangeWhy} — the lines below are still {formatDate(range.from)} – {formatDate(range.to)}.</div>}

      <div className="pc-stages" role="tablist" aria-label="Stages">
        <button type="button" className="pc-expand-all" onClick={toggleAll} disabled={pageEntries ? !pageCustomerKeys.length : !pageRows.length}
          title={pageEntries ? (allOpen ? 'Collapse all — every customer as a single row' : 'Expand all — every customer open, its products closed')
            : (allOpen ? 'Collapse every line on this page' : 'Expand every line on this page')} aria-label={allOpen ? 'Collapse all' : 'Expand all'}>
          <span className="material-symbols-rounded">{allOpen ? 'unfold_less' : 'unfold_more'}</span>
        </button>
        {STAGE_TABS.map((t, i) => {
          // Wider apart after To do; All at the end of the row (Cam, 29 Oct 2026).
          const sep = (i === 1 || t.key === 'all')
            ? <span className={`pc-stage-sep${t.key === 'all' ? ' end' : ' wide'}`} aria-hidden="true" key={`sep-${t.key}`} /> : null;
          const arrow = !t.side && i > 1 ? <span className="material-symbols-rounded pc-stage-arrow" aria-hidden="true">chevron_right</span> : null;
          const note = t.key === 'needs'
            ? null
            : t.note;
          return (
            <React.Fragment key={t.key}>
              {sep}{arrow}
              <div className={`pc-stage${t.side ? ' side' : ''}${tab === t.key ? ' on' : ''}`}>
                <button type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
                  title={t.key === 'needs'
                    ? 'No cost or no sale price to propose — and lines whose price is with a rep while their cost is not set'
                    : t.key === 'todo' ? 'Every line not yet confirmed (costed lines with reps included)' : ''}>
                  <span className="pc-stage-l">{t.dot && <span className={`pc-dot p-${t.dot}`} />}{t.label}</span>
                  <span className="pc-stage-n">{counts[t.key] || 0}<span className="pc-stage-u"> line{(counts[t.key] || 0) === 1 ? '' : 's'}</span></span>
                  {note !== null && <span className="pc-stage-x">{note || '\u00a0'}</span>}
                </button>
                {t.key === 'needs' && (
                  <button type="button" role="tab" className={`pc-stage-sub${tab === 'with_reps' ? ' on' : ''}`} aria-selected={tab === 'with_reps'}
                    onClick={() => setTab('with_reps')} disabled={!counts.with_reps && tab !== 'with_reps'}
                    title="Lines whose sale price is with a rep and whose cost is set or confirmed — in To do, not in Needs a price. Click to see them.">
                    + {counts.with_reps || 0} line{(counts.with_reps || 0) === 1 ? '' : 's'} costed, with reps
                  </button>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {selected.length > 0 && (
        <div className="pc-bulk">
          <span>{selected.length} line{selected.length === 1 ? '' : 's'} ticked{(() => { const o = new Set(selectedRows.filter(v => v.order_id).map(v => v.order_id)).size; return o ? ` · ${o} order${o === 1 ? '' : 's'}` : ''; })()}</span>
          <button type="button" className="pc-chip" onClick={() => setMany(filtered.map(v => v.key), true)}>Select all {filtered.length} line{filtered.length === 1 ? '' : 's'} in this stage</button>
          <button type="button" className="pc-chip" disabled={busy || readOnly} onClick={() => confirmRows(selectedRows, ['cost'])}>
            <span className="material-symbols-rounded">shopping_cart_checkout</span>Confirm costs</button>
          <button type="button" className="pc-chip" disabled={busy || readOnly} onClick={() => confirmRows(selectedRows, ['sale'])}>
            <span className="material-symbols-rounded">sell</span>Confirm prices</button>
          <button type="button" className="pc-chip on" disabled={busy || readOnly} onClick={() => confirmRows(selectedRows, ['cost', 'sale'])}>
            <span className="material-symbols-rounded">done_all</span>Confirm both</button>
          {setActions(selectedRows, '', 'bb').map(a => (
            <button key={a.key} type="button" className="pc-chip" disabled={a.disabled || busy} title={a.title}
              onClick={a.onClick}><span className="material-symbols-rounded">{a.icon}</span>{a.label.replace('…', ` (${selectedRows.length})…`)}</button>
          ))}
          <button type="button" className="pc-chip" disabled={!!sendBlock(selectedRows)} title={sendBlock(selectedRows) || 'One email per rep, each checked before it goes'}
            onClick={() => openSend(selectedRows)}>
            <span className="material-symbols-rounded">forward_to_inbox</span>Review &amp; send to reps ({selectedRows.filter(sendable).length} lines)</button>
          <button type="button" className="pc-chip" disabled={busy || !!contractBlock(selectedRows)}
            title={contractBlock(selectedRows) || 'One contract per customer and product — several products of one customer can be one contract'}
            onClick={() => openContract(selectedRows)}>
            <span className="material-symbols-rounded">contract_edit</span>Contract these prices ({selectedRows.filter(v => v.kind === 'order_line').length} lines)</button>
          {selectedRows.some(v => sendable(v) && !v.sale?.notForReps) && (
            <button type="button" className="pc-chip" disabled={busy || !!exclusionBlock(selectedRows, true)} title={exclusionBlock(selectedRows, true) || 'Price them without asking a rep'}
              onClick={() => runExclusion(selectedRows, true)}><span className="material-symbols-rounded">do_not_disturb_on</span>Remove from Send to reps</button>
          )}
          {selectedRows.some(v => sendable(v) && v.sale?.notForReps) && (
            <button type="button" className="pc-chip" disabled={busy || !!exclusionBlock(selectedRows, false)} title={exclusionBlock(selectedRows, false) || ''}
              onClick={() => runExclusion(selectedRows, false)}><span className="material-symbols-rounded">add_circle</span>Put back in Send to reps</button>
          )}
          {selectedRows.some(v => v.kind === 'xero_po_line') && (
            <button type="button" className="pc-chip" disabled={busy || !!xeroInvoiceBlock(selectedRows)} title={xeroInvoiceBlock(selectedRows) || 'One invoice per customer'}
              onClick={() => openXeroInvoice(selectedRows)}><span className="material-symbols-rounded">request_quote</span>Invoice Xero PO lines ({selectedRows.filter(v => v.kind === 'xero_po_line' && !v.invoice).length} lines)</button>
          )}
          {selectedRows.some(reviewable) && (
            <>
              <button type="button" className="pc-chip" disabled={busy || !!reviewBlock(selectedRows, 'approve')} title={reviewBlock(selectedRows, 'approve') || ''}
                onClick={() => runReview(selectedRows, 'approve')}><span className="material-symbols-rounded">thumb_up</span>Approve rep prices ({selectedRows.filter(reviewable).length} lines)</button>
              <button type="button" className="pc-chip" disabled={busy || !!reviewBlock(selectedRows, 'hold')} title={reviewBlock(selectedRows, 'hold') || ''}
                onClick={() => runReview(selectedRows, 'hold')}><span className="material-symbols-rounded">pause_circle</span>Hold rep prices</button>
            </>
          )}
          {(() => {
            const n = invoiceableOrders(selectedRows).size;
            return (
              <button type="button" className="pc-chip" disabled={busy || !n}
                title={n ? 'Raise the invoice for each order, at the confirmed prices' : 'No ticked order is delivered with every line confirmed'}
                onClick={() => askInvoice(selectedRows)}><span className="material-symbols-rounded">request_quote</span>Invoice {n} order{n === 1 ? '' : 's'}</button>
            );
          })()}
          <button type="button" className="pc-chip" disabled={busy || readOnly || !selectedDrafts.length || !data.can.approve_invoices}
            title={!selectedDrafts.length ? 'No ticked line is on a draft invoice' : !data.can.approve_invoices ? "Needs the 'Create invoices' permission" : ''}
            onClick={() => askApprove(selectedDrafts)}><span className="material-symbols-rounded">task_alt</span>Approve {selectedDrafts.length} draft invoice{selectedDrafts.length === 1 ? '' : 's'}</button>
          <button type="button" className="pc-chip" onClick={() => setSelected([])}><span className="material-symbols-rounded">close</span>Clear</button>
        </div>
      )}

      <div className="table-container" ref={tableRef} onContextMenu={ctx.onTableContextMenu}>
        {loading && !data.loaded ? <LoadingSpinner /> : loadError ? (
          <div className="pc-banner err"><span className="material-symbols-rounded">error</span>{loadError}</div>
        ) : (
          <table className="data-table pc-table">
            <colgroup>
              <col style={{ width: 34 }} />
              <col style={{ width: 106 }} />
              {/* The product takes what is left: with every column sized, a
                  fixed table shares any spare width across all of them. */}
              <col />
              <col style={{ width: '16%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: marginColW }} />
              <col style={{ width: 124 }} />
              <col style={{ width: 82 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="checkbox-col">
                  <TickCheckbox id="pc-select-page" checked={allOnPage} ariaLabel="Select this page"
                    onChange={(e) => setMany(pageRows.map(r => r.key), e.target.checked)} />
                </th>
                <th>Order / PO</th>
                <th>Product</th>
                <th>Cost</th>
                <th>Sale</th>
                <th className="pc-margin">Margin</th>
                <th title="Cost · Sale · Confirmed · Invoiced — green done, blue with a rep, amber needs a look, grey not yet">Progress</th>
                <th className="pc-actions-head" aria-label="Actions" />
              </tr>
            </thead>
            {body.length ? body : (
              <tbody>
                <tr><td colSpan={8} className="pc-empty">
                  <span className="material-symbols-rounded">task_alt</span>
                  {!rangeLoaded ? 'Loading your date range…'
                    : filters.xero && !matching.length ? 'No lines of POs made in Xero deliver in this range.'
                      : tab === 'todo' ? 'Nothing left to do in this range.'
                        : tab === 'with_reps' ? 'No costed line is with a rep.' : 'No lines here.'}
                </td></tr>
              </tbody>
            )}
          </table>
        )}
      </div>
      <PaginationControls
        pagination={{
          page: safePage, limit, total: filtered.length, totalPages,
          // Grouped, pages hold whole product groups, so they vary in length.
          ...(groupPages ? {
            from: groupPages.slice(0, safePage - 1).reduce((n, pg) => n + pg.reduce((m, e) => m + e.request.rows.length, 0), 0) + (pageRows.length ? 1 : 0),
            to: groupPages.slice(0, safePage).reduce((n, pg) => n + pg.reduce((m, e) => m + e.request.rows.length, 0), 0)
          } : {})
        }}
        onPageChange={setPage} />

      <TableContextMenu ctx={ctx} rowActions={menuRowActions} bulkActions={bulkActions} actions={genericActions}
        bulkTitle={(ids) => `Bulk — all ${ids.size} ticked lines`} />

      {toast && (
        <div className="pc-toast">
          <span>{toast.message}</span>
          {toast.withUndo && undoStack.length > 0 && <button type="button" onClick={undo}>Undo</button>}
          {toast.action && <button type="button" onClick={() => { const f = toast.action.run; setToast(null); f(); }}>{toast.action.label}</button>}
        </div>
      )}

      {priorMonth.dialog}
      <VariationReview isOpen={variationOpen} onClose={() => setVariationOpen(false)} user={user}
        initialFrom={range.from} initialTo={range.to} />
      {approveHere.dialog}
      {invoicePriorMonth.dialog}
      {invoiceAsk && (
        <InvoiceBookOverDialog
          orders={invoiceAsk.orders}
          message={invoiceAsk.message}
          confirmText={invoiceAsk.confirmText}
          onCancel={() => setInvoiceAsk(null)}
          onConfirm={(bookOver) => { const ids = invoiceAsk.orders.map(o => o.id); setInvoiceAsk(null); runInvoice(ids, bookOver); }}
        />
      )}
      <ConfirmDialog
        isOpen={!!confirmAsk}
        title={confirmAsk?.title}
        message={confirmAsk?.message}
        confirmText={confirmAsk?.confirmText || 'Confirm'}
        type={confirmAsk?.secondaryText !== undefined ? 'warning' : 'info'}
        secondaryText={confirmAsk?.secondaryText || null}
        secondaryIcon="filter_alt"
        onSecondary={confirmAsk?.onSecondary ? () => { const f = confirmAsk.onSecondary; setConfirmAsk(null); f(); } : null}
        onConfirm={() => { const f = confirmAsk.onConfirm; setConfirmAsk(null); f(); }}
        onCancel={() => setConfirmAsk(null)}
      />
      <AlertDialog isOpen={alert.isOpen} message={alert.message} type={alert.type}
        onClose={() => setAlert({ isOpen: false, message: '', type: 'info' })} />
      {bookingOverId && (
        <BookOverInvoiceFlow invoiceId={bookingOverId}
          onDone={() => load()}
          onClose={() => { setBookingOverId(null); load(); }} />
      )}
      {sendLines && (
        <RepSendModal
          lineIds={sendLines.ids}
          costs={sendLines.costs}
          resendOf={sendLines.resendOf || null}
          onClose={(sent) => {
            setSendLines(null);
            if (sent) {
              // Lines asked about leave the rep view; untick them.
              const keys = new Set(sendLines.ids.map(id => `ol:${id}`));
              setSelected(prev => prev.filter(k => !keys.has(k)));
            }
            load();
          }}
        />
      )}
      {showSent && (
        <RepRequestHistoryModal
          canResend={!readOnly && !!data.can.confirm_sales}
          onClose={() => setShowSent(false)}
          onSendAgain={(send, ids) => {
            // Today's figures for that send's lines, checked before it goes;
            // recorded as asked again from it.
            setShowSent(false);
            setSendLines({ ids, costs: {}, resendOf: /^\d+$/.test(String(send.id)) ? Number(send.id) : null });
          }}
        />
      )}
      {invoiceJob?.id && (
        <InvoicingProgressModal
          jobId={invoiceJob.id}
          start={invoiceJob.start}
          onClose={() => { setInvoiceJob(null); setSelected([]); load(); }}
        />
      )}
      {xeroInvoiceRows && customers && (
        <XeroPoInvoiceModal
          rows={xeroInvoiceRows}
          customers={customers}
          onClose={(done) => {
            const keys = new Set(xeroInvoiceRows.map(r => r.key));
            setXeroInvoiceRows(null);
            if (done) setSelected(prev => prev.filter(k => !keys.has(k)));
            load();
          }}
        />
      )}
      {importing && (
        <RepRepliesImportModal reviewReady={!!data.rep_review_ready} lineFacts={repLineFacts}
          onClose={() => { setImporting(false); load(); }}
          onDone={(message, undoItems = [], stayOpen = false) => {
            // Lines the import confirmed (approved, or set to the contract) are one Undo.
            if (undoItems.length) setUndoStack(prev => [...prev.slice(-9), undoItems]);
            if (!stayOpen) setImporting(false);
            if (message) showToast(message, undoItems.length > 0);
            load();
          }} />
      )}
      {setAsk && (
        <PricingSetPopover
          title={setAsk.title}
          rows={setAsk.keys.map(k => viewByKey.get(k)).filter(Boolean)}
          sides={setAsk.sides}
          per={viewByKey.get(setAsk.keys[0])?.per || ''}
          x={setAsk.x} y={setAsk.y}
          readOnly={readOnly}
          confirmBlock={{ cost: confirmBlockFor('cost') || (busy ? 'Busy' : null), sale: confirmBlockFor('sale') || (busy ? 'Busy' : null) }}
          labelOf={(k) => lineLabelOf(viewByKey.get(k) || {})}
          enterPer={enterPer}
          onPerChange={(mixed, p, side) => setEnterPer(prev => ({ ...prev, [`${mixed ? 'mixed' : 'single'}_${side}`]: p }))}
          onApply={(bySide, confirm) => applySet(setAsk, bySide, confirm)}
          onClose={() => setSetAsk(null)}
        />
      )}
      {historyFor && <PricingHistoryModal {...historyFor} seeCosts={!!data.can.see_costs} onClose={() => { setHistoryFor(null); load(); }} />}
      {contractLines && (
        <ContractPricesModal lines={contractLines.lines} notLines={contractLines.notLines}
          onClose={() => { setContractLines(null); load(); }}
          onDone={(message) => { setContractLines(null); if (message) showToast(message); load(); }} />
      )}
    </div>
  );
}

export default PricingCosting;
